import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import AbstractComponent from "./abstract-component";
import {
	MOTION,
	prefersReducedMotion,
	isCoarseOrNarrow,
	motionDistance,
	motionDuration,
	motionStagger,
	motionIo,
} from "../motion/tokens";
import {
	isMediaReveal,
	prepareMediaRevealNode,
	settleMediaRevealNode,
	tweenMediaReveal,
} from "../motion/media-reveal";
import { waitHeroTextDone } from "../motion/hero-gate";
import { allowHyphenBreaks, flattenLineWords } from "../motion/lines";

gsap.registerPlugin(CustomEase);
CustomEase.create("feuTitle", "0.2, 0.6, 0.35, 1");
CustomEase.create("feuCurtain", "0.6, 0, 0.3, 1");

let SplitTypeLib = null;
let assetsWarmPromise = null;

async function getSplitType() {
	if (!SplitTypeLib) {
		const mod = await import(/* webpackChunkName: "split-type" */ "split-type");
		SplitTypeLib = mod.default || mod;
	}
	return SplitTypeLib;
}

function waitFonts() {
	if (document.fonts?.ready) return document.fonts.ready.catch(() => {});
	return Promise.resolve();
}

/** Préchauffe fonts + SplitType (appelable depuis le loader). */
export function preloadRevealAssets() {
	if (!assetsWarmPromise) {
		assetsWarmPromise = Promise.all([waitFonts(), getSplitType().catch(() => null)]);
	}
	return assetsWarmPromise;
}

/**
 * Éléments révélés LIGNE PAR LIGNE (masque) : titres + textes importants
 * (surtitres, chapôs courts) marqués [data-reveal-lines]. Le reste du texte = fondu doux.
 */
const LINES_SEL = ".st2, h1.st1, [data-reveal-lines]";

/**
 * Card avec media reveal : la card reste visible (sinon le masque de l'image serait caché),
 * donc ses textes « simples » (sous-titre, contenu, lien…) ont leur propre fondu montant,
 * calé sur l'arrivée de la card. Titres en lignes / masques / media : déjà animés à part.
 */
function mediaCardParts(item) {
	if (!item?.children || !item.querySelector?.("[data-reveal-media]")) return [];
	const SKIP = "[data-reveal-media], [data-reveal-mask], [data-reveal-soft], " + LINES_SEL;
	const parts = [];
	const visible = (n) => n.getClientRects().length > 0 && n.textContent.trim().length > 0;
	/*
	 * Descend dans les enveloppes (ex. article-card : <article> > cover + __content) :
	 * branche avec media → on y descend ; bloc texte à plusieurs enfants → chaque enfant
	 * (pastille/date, titre, extrait) ; bloc simple → lui-même.
	 */
	const walk = (el, depth) => {
		Array.from(el.children).forEach((c) => {
			if (c.matches(SKIP) || !visible(c)) return;
			const hasMedia = !!c.querySelector("[data-reveal-media]");
			const hasOwnAnim = !!c.querySelector("[data-reveal-mask], " + LINES_SEL);
			const container = /^(DIV|ARTICLE|HEADER|FOOTER|SECTION|UL|OL)$/.test(c.tagName);
			if ((hasMedia || hasOwnAnim || c.children.length > 1) && container && depth < 3 && c.children.length) {
				walk(c, depth + 1);
				return;
			}
			if (!hasMedia && !hasOwnAnim) parts.push(c);
		});
	};
	walk(item, 0);
	return parts;
}

/** Rang de chaque media dans sa ligne visuelle (même haut ±48px), trié gauche → droite. */
function mediaRowRanks(hosts) {
	const ranks = new Map();
	if (hosts.length < 2) return ranks;
	const boxes = hosts.map((h) => ({ h, r: h.getBoundingClientRect() })).filter((b) => b.r.width > 0);
	boxes.sort((a, b) => a.r.top - b.r.top || a.r.left - b.r.left);
	let row = [];
	const flush = () => {
		row.sort((a, b) => a.r.left - b.r.left).forEach((b, i) => ranks.set(b.h, i));
		row = [];
	};
	boxes.forEach((b) => {
		if (row.length && Math.abs(b.r.top - row[0].r.top) > 48) flush();
		row.push(b);
	});
	flush();
	return ranks;
}

function settleMediaCardParts(item) {
	(item?.__cardParts || []).forEach((n) => gsap.set(n, { clearProps: "opacity,visibility,transform" }));
	if (item) item.__cardParts = null;
}
/** Textes « importants » révélés en lignes (si courts) — sinon fondu. */
const LEAD_LINES_SELECTOR = [
	".c-four-push__subtitle",
	".c-faq__intro",
	".c-articles-list__header__description",
	".c-contact__hero-text",
	".c-push-crossnav__desc",
	".c-article-header__intro",
].join(", ");
const LEAD_MAX_CHARS = 320;

/** Soft/item/decor/titres appartenant à ce bloc (pas à un [data-reveal] nested). */
function queryOwned(root, selector) {
	if (!root?.querySelectorAll) return [];
	return Array.from(root.querySelectorAll(selector)).filter((n) => {
		const owner = n.closest?.("[data-reveal]");
		return !owner || owner === root;
	});
}

/** Parallax owns transform (y/x) — reveal must not fight it (jump scroll). */
function isParallaxNode(n) {
	return !!n?.matches?.("[data-parallax], [data-parallax-speed], [data-parallax-mouse]");
}

/** Fade-only : parallax node, ou géré par Swiper testimony (évite double y). */
function revealFadeOnly(n) {
	if (!n) return false;
	if (isParallaxNode(n)) return true;
	/* Quote/media déjà animés par testimony.js — pas de softY/itemY Reveal par-dessus */
	if (n.closest?.(".c-testimony__track__quotes__item, .c-testimony__track__images__item")) return true;
	return false;
}

/** Slide-up sans scale (média text-image). */
function isRiseReveal(n) {
	return !!n?.hasAttribute?.("data-reveal-rise");
}

/** Chrome visuellement rattaché au header — visible à l’arrivée, pas de scroll reveal. */
const HEADER_ATTACHED_SELECTOR =
	".c-header__profile-bar, [data-reveal-at-load], .c-articles-list__filters";

function isHeaderAttachedNode(n) {
	return !!n?.matches?.(HEADER_ATTACHED_SELECTOR);
}

function isHeaderAttachedReveal(el) {
	return isHeaderAttachedNode(el);
}

function showHeaderAttachedNode(n) {
	if (!n) return;
	n.classList?.remove?.("is-reveal-pending");
	n.__revealed = true;
	n.__revealStarted = true;
	n.__revealPrepared = true;
	gsap.set(n, { autoAlpha: 1, visibility: "visible", y: 0, scale: 1, clearProps: "transform" });
	queryOwned(n, "[data-reveal-soft]").forEach((child) => {
		gsap.set(child, { autoAlpha: 1, y: 0, scale: 1, clearProps: "transform" });
	});
}

function flushHeaderAttachedNodes(root) {
	if (!root?.querySelectorAll) return;
	root.querySelectorAll(HEADER_ATTACHED_SELECTOR).forEach(showHeaderAttachedNode);
}

/** Hero intro copy (desc / surtitle / CTA) : opacity-only — pas de y/scale (CLS). */
function setHeroIntroHiddenLayout(el) {
	if (!el) return;
	gsap.set(el, { opacity: 0, visibility: "visible", y: 0, scale: 1, clearProps: "transform" });
}

function showHeroIntroCopy(blocks) {
	(blocks || []).filter(Boolean).forEach((el) => {
		gsap.set(el, { opacity: 1, visibility: "visible" });
	});
}

function finalizeHeroIntroLayout(titles, copyBlocks) {
	titles?.forEach((h1) => {
		h1.classList?.remove?.("is-reveal-pending");
		h1.style.visibility = "visible";
		lockHeroBlockHeight(h1);
	});
	copyBlocks?.filter(Boolean).forEach((el) => {
		lockHeroBlockHeight(el);
		gsap.set(el, { opacity: 1, visibility: "visible" });
	});
}

function lockHeroBlockHeight(el) {
	if (!el) return;
	const h = el.offsetHeight;
	if (h > 0) el.style.minHeight = `${h}px`;
}

function releaseHeroBlockHeight(el) {
	if (!el) return;
	const before = el.offsetHeight;
	if (!el.style.minHeight) return;
	el.style.minHeight = "";
	if (el.offsetHeight < before - 1) {
		el.style.minHeight = `${before}px`;
	}
}

function lockHeroTitleHeight(h1) {
	lockHeroBlockHeight(h1);
}

function releaseHeroTitleHeight(h1) {
	releaseHeroBlockHeight(h1);
}

/** Figé la hauteur hero pendant SplitType / intro — évite CLS header.
 * Cap viewport : un lock gonflé (split / fonts) ne doit jamais dépasser l’écran.
 * Remount : clear height + minHeight (sinon le cliquet ne fait que monter).
 */
function lockHeaderFootprint(header, { remount = false } = {}) {
	if (!header) return;
	/* Fullscreen home : un lock height recadre object-fit = déplacement d’image */
	if (
		header.classList?.contains?.("is-fullscreen") ||
		header.closest?.(".is-fullscreen") ||
		header.classList?.contains?.("c-contact") ||
		header.classList?.contains?.("c-articles-list")
	) {
		return;
	}
	/*
	 * Hero empilé (mobile / tablette < 1190) : hauteur pilotée par le contenu.
	 * Le figer (souvent plafonné à l'écran, et mesuré avant le chargement final des
	 * polices sur iOS) changeait la hauteur du cadre image à la fin de l'intro →
	 * l'image en object-fit:cover était recadrée d'un coup (« saut » iPhone, pages services).
	 */
	if (window.matchMedia("(max-width: 1189.98px)").matches) return;
	const shell = header.closest?.(".c-header-shell");
	const vh = Math.round(window.innerHeight || 0);
	[shell, header].filter(Boolean).forEach((el) => {
		if (remount || el.dataset.heroFootprint === "1") {
			el.style.removeProperty("height");
			el.style.removeProperty("min-height");
		}
		void el.offsetHeight;
		const cssMin = parseFloat(getComputedStyle(el).minHeight) || 0;
		const rectH = Math.round(el.getBoundingClientRect().height);
		let h = Math.max(rectH, Math.round(cssMin));
		/* Cap écran : seulement le surplus du min-height CSS, jamais sous la hauteur réelle
		 * (sinon cadre rétréci pendant l'intro puis agrandi au relâchement = image qui saute) */
		if (vh > 0 && h > vh) h = Math.max(rectH, vh);
		if (h < 1) return;
		/*
		 * min-height SEUL (plus de height) : une hauteur explicite rendait « définis » les
		 * height:100% des enfants (container / row--middle) → contenu centré autrement
		 * pendant l'intro, puis chute d'un coup au relâchement (hero texte seul : +55px).
		 * min-height empêche le rétrécissement pendant le split sans changer la mise en page.
		 */
		el.style.removeProperty("height");
		el.style.minHeight = `${h}px`;
		el.dataset.heroFootprint = "1";
	});
}

/** Relâche height/minHeight inline — le SCSS (déjà plafonné viewport) reprend la main. */
function releaseHeaderFootprint(header) {
	if (!header) return;
	const shell = header.closest?.(".c-header-shell");
	[shell, header].filter(Boolean).forEach((el) => {
		if (el.dataset.heroFootprint !== "1") return;
		el.style.removeProperty("height");
		el.style.removeProperty("min-height");
		delete el.dataset.heroFootprint;
	});
}

/** Préarme shell media sous le loader / rideau Barba (overlay prêt, jamais de translate). */
function armHeroMediaShell(mediaShell, mobile = false, { fullscreen: _fullscreen = false, force = false } = {}) {
	if (!mediaShell) return;
	const mediaHosts = mediaShell.querySelectorAll?.("[data-reveal-media]");

	if (mediaHosts?.length) {
		mediaHosts.forEach((host) => prepareMediaRevealNode(host, mobile, { force }));
	} else {
		const bits = mediaShell.querySelectorAll?.("img, video");
		if (bits?.length) {
			const fresh = ![...bits].some((b) => b.__mediaRevealPrepared);
			if (force || fresh) {
				gsap.set(bits, { y: 0, scale: 1, clearProps: "transform" });
			}
		}
	}

	/* Jamais de Y sur le cadre — home full-bleed ET master card (déplacement bizarre). */
	gsap.set(mediaShell, {
		y: 0,
		opacity: 1,
		visibility: "visible",
		clearProps: "transform",
	});
}

/** Settle immédiat du media hero (reduced-motion / déjà joué / filet). */
function settleHeroMediaShell(mediaShell, mediaIntroTargets = [], { fullscreen = false } = {}) {
	/* Dézoom home en cours : ne pas le couper (le settle remet scale 1 d'un coup) */
	if (mediaShell?.__zooming) return;
	const shells = mediaIntroTargets.length ? mediaIntroTargets : mediaShell ? [mediaShell] : [];
	shells.forEach((el) => {
		if (fullscreen) {
			gsap.set(el, { y: 0, opacity: 1, clearProps: "transform" });
		} else {
			gsap.set(el, { y: 0, opacity: 1, clearProps: "transform" });
		}
	});
	mediaShell?.querySelectorAll?.("[data-reveal-media]")?.forEach((n) => settleMediaRevealNode(n));
	if (mediaShell && !mediaShell.querySelector?.("[data-reveal-media]")) {
		mediaShell.querySelectorAll?.("img, video")?.forEach((bit) => {
			gsap.set(bit, { scale: 1, clearProps: "transform" });
		});
	}
}

function collectTopRevealBlocks(host) {
	if (!host) return [];
	if (host.matches?.("[data-reveal]")) return [host];
	return Array.from(host.querySelectorAll("[data-reveal]")).filter((n) => {
		const outer = n.parentElement?.closest?.("[data-reveal]");
		return !outer;
	});
}

/** Steps / meet : 2 panneaux glissent l’un vers l’autre (contenu lié au fond). */
function isMeetReveal(el) {
	return !!el?.matches?.("[data-reveal-meet], .c-steps");
}

/** Slider réalisations : panel entier depuis la droite. */
function isSliderReveal(el) {
	return !!el?.matches?.("[data-reveal-slider], .c-slider");
}

/** Text-image thémé : panel coloré + texte glissent ; image statique. */
function isTextImagePanelReveal(el) {
	return !!el?.matches?.(
		"[data-reveal-text-image-panel], .c-text-image--granit, .c-text-image--ebene, .c-text-image--sauge"
	);
}

function isPanelSlideReveal(el) {
	return isSliderReveal(el) || isTextImagePanelReveal(el);
}

function collectMeetPanes(el) {
	const left = queryOwned(el, '[data-reveal-pane="left"]');
	const right = queryOwned(el, '[data-reveal-pane="right"]');
	if (left.length || right.length) return { left, right };
	/* Fallback sélecteurs steps si attrs absents */
	return {
		left: [
			el.querySelector?.(".c-steps__intro"),
			el.querySelector?.(".c-steps__deco"),
		].filter(Boolean),
		right: [el.querySelector?.(".c-steps__panel")].filter(Boolean),
	};
}

function collectSliderPanel(el) {
	const pane = queryOwned(el, '[data-reveal-pane="panel"]');
	if (pane.length) return pane;
	const panel = el.querySelector?.(".c-slider__panel");
	return panel ? [panel] : [];
}

function collectTextImagePanel(el) {
	const pane = queryOwned(el, '[data-reveal-pane="panel"]');
	if (pane.length) return pane;
	const panel = el.querySelector?.(".container");
	return panel ? [panel] : [];
}

function collectPanelSlideTargets(el) {
	if (isSliderReveal(el)) return collectSliderPanel(el);
	if (isTextImagePanelReveal(el)) return collectTextImagePanel(el);
	return [];
}

/** Batch 3 — travel vertical (cabine) pour panels meet / text-image / slider. */
function panelSlideTravelY(mobile = isCoarseOrNarrow()) {
	return motionDistance("itemY", mobile) ?? 36;
}

/**
 * Intro media hero : le shell radius (`.c-header__background`), pas l’img/vidéo.
 * TranslateY soft sur le bloc clipé — image calée dedans, zéro zoom.
 */
function collectHeroMediaIntroTargets(mediaShell) {
	return mediaShell ? [mediaShell] : [];
}

/** Positions initiales meet — rise Y only (pas de fade opacity). */
function setMeetPaneOffsets(left, right, mobile = isCoarseOrNarrow()) {
	const y = panelSlideTravelY(mobile);
	(left || []).forEach((n) => {
		const isDeco = n.classList?.contains?.("c-steps__deco");
		gsap.set(n, {
			autoAlpha: 1,
			visibility: "visible",
			y,
			force3D: true,
			zIndex: isDeco ? 2 : 3,
		});
	});
	if (right?.length) {
		gsap.set(right, {
			autoAlpha: 1,
			visibility: "visible",
			y,
			zIndex: 1,
			force3D: true,
		});
	}
	return y;
}

/** ACF wysiwyg wrappe souvent un titre dans un seul <p> — casse SplitType. */
function unwrapSingleParagraph(el) {
	if (!el || el.children.length !== 1 || el.children[0].tagName !== "P") return;
	const p = el.children[0];
	while (p.firstChild) el.insertBefore(p.firstChild, p);
	p.remove();
}

/**
 * Line-mask (legacy): overflow clip on .split-line, animate inner rise.
 */
function wrapLineInners(lines) {
	const inners = [];
	lines.forEach((line) => {
		/* SplitType bake text-align au moment du split — laisse le CSS responsive piloter */
		line.style.removeProperty("text-align");
		flattenLineWords(line);
		if (line.querySelector(":scope > .split-line__inner")) {
			inners.push(line.querySelector(":scope > .split-line__inner"));
			return;
		}
		const inner = document.createElement("span");
		inner.className = "split-line__inner";
		while (line.firstChild) inner.appendChild(line.firstChild);
		line.appendChild(inner);
		inners.push(inner);
	});
	return inners;
}

/**
 * Word-mask FEU: overflow clip on .split-word, animate .split-word__inner rise.
 */
function wrapWordInners(words) {
	const inners = [];
	(words || []).forEach((word) => {
		if (word.querySelector(":scope > .split-word__inner")) {
			inners.push(word.querySelector(":scope > .split-word__inner"));
			return;
		}
		const inner = document.createElement("span");
		inner.className = "split-word__inner";
		while (word.firstChild) inner.appendChild(word.firstChild);
		word.appendChild(inner);
		inners.push(inner);
	});
	return inners;
}

/**
 * Titres v2 : masque LIGNE par ligne (réf. éditoriale pda.archi).
 * Chaque ligne = .split-line (overflow clip) > .split-line__inner (translate).
 * Le split est retiré dès la fin de l'animation (_revertTitleSplit) : le titre
 * retrouve son HTML d'origine → reflow responsive, sélection et a11y intacts.
 */
function splitTitleIntoLines(title, SplitType) {
	unwrapSingleParagraph(title);
	allowHyphenBreaks(title);
	const split = new SplitType(title, {
		types: "lines",
		tagName: "span",
		lineClass: "split-line",
	});
	const inners = wrapLineInners(split.lines || []);
	inners.__title = title;
	title.__split = split;
	title.__inners = inners;
	title.__useLineMask = true;
	gsap.set(inners, { yPercent: motionDistance("lineYPercent") ?? 110 });
	return { split, inners };
}

/** Tween standard des lignes d'un titre (blocs, orphelins, panels). */
function titleLinesTween(inners, mobile, onDone) {
	return {
		yPercent: 0,
		duration: motionDuration("title", mobile),
		stagger: motionStagger("lines", mobile),
		ease: MOTION.ease.title,
		force3D: true,
		onComplete: onDone,
	};
}

function isInViewportBand(el, vh = window.innerHeight || 0) {
	if (!el) return false;
	const r = el.getBoundingClientRect();
	if (r.height <= 0 && r.width <= 0) return false;
	/* bandTop ~0.75 : +15% plus tard — l’anim part quand le module est bien dans l’écran */
	const io = motionIo("blocks");
	return r.bottom > vh * io.bandBottom && r.top < vh * io.bandTop;
}

/** Entièrement (ou quasi) au-dessus du viewport — l’utilisateur a scrollé trop vite. */
function isScrolledPast(el, vh = window.innerHeight || 0) {
	if (!el) return false;
	const r = el.getBoundingClientRect();
	return r.bottom < vh * 0.06;
}

function getBreakpointBucket(width = window.innerWidth || 0) {
	if (width < 576) return "xs";
	if (width < 880) return "sm";
	if (width < 1190) return "md";
	return "lg";
}

function getLineHeight(el) {
	const styles = getComputedStyle(el);
	const computed = parseFloat(styles.lineHeight);
	if (!Number.isNaN(computed)) return computed;
	const fontSize = parseFloat(styles.fontSize) || 16;
	return fontSize * 1.2;
}

/** Masque ligne sur tous les écrans (lignes peu nombreuses = coût négligeable en mobile). */
function shouldUseLineMask(el, { mobile: _mobile = false } = {}) {
	if (!el) return false;
	return !prefersReducedMotion();
}

/** Transition Barba en cours et contenu pas encore révélé. */
function pageStillCovered() {
	const html = document.documentElement;
	return (
		html.classList.contains("is-transitioning") &&
		window.__drieuxTransitionReveal !== true &&
		!html.classList.contains("is-transition-done")
	);
}

export default class Reveal extends AbstractComponent {
	constructor(el) {
		super(el);
		this._started = false;
		this._destroyed = false;
		this._introDone = false;
		this._introStarted = false;
		this._introTextDone = false;
		this._introTl = null;
		this._heroMediaTl = null;
		this.DOM = {};
		this._splits = [];
		this._ioBlocks = null;
		this._ioTitles = null;
		this._onResize = null;
		this._onVisibilityChange = null;
		this._resizeTimer = null;
		this._safetyTimer = null;
		this._onLoaderDone = null;
		this._onLoaderReveal = null;
		this._onTransitionDone = null;
		this._onTransitionReveal = null;
		this._onEnableScroll = null;
		this._loaderGuard = null;
		this._onWinFlush = null;
		this._onLenisFlush = null;
		this._scrollFlushRaf = null;
		this._scrollFlushBound = false;
		this._fullscreenHero = false;
		this._breakpointBucket = getBreakpointBucket();
		this._lastSplitWidth = window.innerWidth || 0;
		this._reduced = prefersReducedMotion();
		/* Mobile = profil allégé — PAS showAllImmediate (sauf reduced-motion) */
		this._mobile = isCoarseOrNarrow();
		this._skipHeavy = this._reduced;
		this._activeRevealCount = 0;
		this._maxConcurrentReveals = 4;
		this._revealQueue = [];
		this._assetsReady = preloadRevealAssets();

		this.$header = null;
		this._copyOnlyIntro = false;
		const headerHost = this.$el.closest?.(".c-header, .c-header-simple") || null;
		if (
			headerHost &&
			(this.$el === headerHost ||
				this.$el.querySelector?.("h1.st1, .c-header__title, .c-header-simple__title"))
		) {
			this.$header = headerHost;
		} else {
			const pageHero = this.$el.closest?.(".c-contact, .c-articles-list") || null;
			const hasPageTitle = !!this.$el.querySelector?.(
				".c-contact__hero-title, .c-articles-list__header__title"
			);
			/* Intro titre sans media — pas de [data-reveal] sur le host (évite double anim). */
			if (pageHero && hasPageTitle && !this.$el.matches?.("[data-reveal]")) {
				this.$header = pageHero;
				this._copyOnlyIntro = true;
			}
		}

		this.initDom();

		if (this._skipHeavy) {
			this.showAllImmediate();
			return;
		}

		/* Profile bar / filtres hero : pas d’IO scroll — chrome visible dès le load */
		if (isHeaderAttachedReveal(this.$el)) {
			showHeaderAttachedNode(this.$el);
			return;
		}

		this.wireReveals();
		this._start = this._start.bind(this);
		this._maybeDeferToLoader();
	}

	/**
	 * Filet post-loader uniquement : above-the-fold coincé après start.
	 * Ne JAMAIS flusher les blocs hors viewport (sinon scroll-reveals morts).
	 */
	_armSafetyFlush() {
		if (this._safetyTimer) clearTimeout(this._safetyTimer);
		const delayMs = Math.ceil((MOTION.safetyIntroSec || 2.2) * 1000);
		this._safetyTimer = setTimeout(() => {
			if (this._destroyed || this._skipHeavy) return;
			this._rescueAboveFold();
		}, delayMs);
	}

	_rescueAboveFold() {
		const vh = window.innerHeight || 0;

		/* Hero encore caché alors que start aurait dû tourner */
		if (this.$header && !this._introDone) {
			const heroStuck = this.DOM.titlesSt1?.some((h1) => {
				const s = getComputedStyle(h1);
				return s.visibility === "hidden" || s.opacity === "0";
			});
			if (heroStuck) {
				this.showAllImmediate();
				return;
			}
		}

		this.DOM.revealBlocks?.forEach((el) => {
			if (el.__revealed) return;
			if (!isInViewportBand(el, vh)) return;
			/* Ne pas snapper un bloc jamais lancé — animer. Snap seulement si coincé. */
			if (el.__revealStarted && !el.__revealed) {
				const startedAt = el.__revealStartedAt || 0;
				if (startedAt && Date.now() - startedAt > 2200) {
					this._forceShowBlock(el);
					this._ioBlocks?.unobserve?.(el);
				}
				return;
			}
			this.animateBlock(el);
			if (el.__revealed) this._ioBlocks?.unobserve?.(el);
		});

		this.DOM.orphanTitles?.forEach((title) => {
			if (!title.classList.contains("is-reveal-pending")) return;
			if (!isInViewportBand(title, vh)) return;
			this._forceShowOrphanTitle(title);
			this._ioTitles?.unobserve?.(title);
		});
	}

	_forceShowOrphanTitle(title) {
		if (!title) return;
		title.classList.remove("is-reveal-pending");
		title.style.visibility = "visible";
		gsap.set(title, { visibility: "visible", autoAlpha: 1, y: 0, clearProps: "transform" });
		this._resetSplitInners(title);
	}

	_forceShowBlock(el) {
		if (!el) return;
		el.__meetTl?.kill?.();
		el.__meetTl = null;
		el.__sliderTl?.kill?.();
		el.__sliderTl = null;
		el.__blockTl?.kill?.();
		el.__blockTl = null;
		this._clearBlockSafety(el);
		el.__revealed = true;
		el.__revealStarted = true;
		el.__revealQueued = false;
		el.classList?.remove?.("is-reveal-pending");
		gsap.set(el, { autoAlpha: 1, y: 0, clearProps: "transform" });
		queryOwned(el, LINES_SEL + ", h1").forEach((t) => {
			t.classList.remove("is-reveal-pending");
			t.style.visibility = "visible";
			gsap.set(t, { autoAlpha: 1, visibility: "visible", y: 0, clearProps: "transform" });
			this._resetSplitInners(t);
			this._revertTitleSplit(t);
		});
		queryOwned(el, "[data-reveal-item], [data-reveal-soft], [data-reveal-decor]").forEach((n) => {
			if (revealFadeOnly(n)) {
				gsap.set(n, { autoAlpha: 1 });
				return;
			}
			if (isRiseReveal(n)) {
				gsap.set(n, { autoAlpha: 1, visibility: "visible", y: 0, yPercent: 0, clearProps: "transform" });
				return;
			}
			if (isMediaReveal(n)) {
				settleMediaRevealNode(n);
				return;
			}
			gsap.set(n, { autoAlpha: 1, y: 0, clearProps: "transform" });
		});
		queryOwned(el, "[data-reveal-media]").forEach((n) => settleMediaRevealNode(n));
		el.querySelectorAll?.("[data-reveal-media]")?.forEach((n) => settleMediaRevealNode(n));
		el.querySelectorAll?.("[data-reveal-mask]")?.forEach((n) => {
			gsap.set(n, { yPercent: 0, clearProps: "transform,transition" });
		});
		el.querySelectorAll?.("[data-reveal-item]")?.forEach((n) => {
			n.__lateDone = true;
			settleMediaCardParts(n);
		});
		if (isMeetReveal(el)) {
			const { left, right } = collectMeetPanes(el);
			left.forEach((n) => {
				const isDeco = n.classList?.contains?.("c-steps__deco");
				gsap.set(n, {
					autoAlpha: 1,
					visibility: "visible",
					y: 0,
					zIndex: isDeco ? 2 : 3,
					clearProps: "transform",
				});
			});
			if (right.length) {
				gsap.set(right, { autoAlpha: 1, visibility: "visible", y: 0, zIndex: 1, clearProps: "transform" });
			}
		}
		if (isSliderReveal(el) || isTextImagePanelReveal(el)) {
			collectPanelSlideTargets(el).forEach((n) => {
				gsap.set(n, { autoAlpha: 1, y: 0, clearProps: "transform" });
			});
		}
	}

	initDom() {
		this.DOM.titlesSt2 = Array.from(this.$el.querySelectorAll(".st2"));
		this.DOM.titlesSt1 = Array.from(this.$el.querySelectorAll("h1.st1, h1"));
		const st1 = Array.from(this.$el.querySelectorAll("h1.st1"));
		if (st1.length) this.DOM.titlesSt1 = st1;

		/* Un seul owner par [data-reveal] — nested = autre instance Reveal */
		this.DOM.revealBlocks = collectTopRevealBlocks(this.$el);

		/* Conteneurs : chaque enfant direct = un bloc reveal indépendant (prose, listes) */
		const childHosts = [];
		if (this.$el.matches?.("[data-reveal-children]")) childHosts.push(this.$el);
		this.$el.querySelectorAll?.("[data-reveal-children]")?.forEach((h) => {
			if (!childHosts.includes(h)) childHosts.push(h);
		});
		childHosts.forEach((host) => {
			Array.from(host.children || []).forEach((child) => {
				if (!(child instanceof HTMLElement)) return;
				if (child.hasAttribute("data-reveal")) return;
				if (this.DOM.revealBlocks.includes(child)) return;
				child.__revealSelf = true;
				this.DOM.revealBlocks.push(child);
			});
		});

		this.DOM.orphanTitles = this.DOM.titlesSt2.filter((t) => !t.closest("[data-reveal]"));

		if (this.$header) {
			if (this._copyOnlyIntro) {
				this.DOM.desc =
					this.$header.querySelector(
						".c-contact__hero-text, .c-articles-list__header__description"
					) || null;
				this.DOM.btns = [];
				this.DOM.surtitle = null;
				this.DOM.actionsEl = null;
				this.DOM.mediaShell = null;
				this.DOM.mediaIntroTargets = [];
				this._fullscreenHero = false;
				if (!this._introDone && !this._introStarted) {
					this.DOM.titlesSt1.forEach((h1) => {
						h1.classList.add("is-reveal-pending");
						h1.style.visibility = "hidden";
						lockHeroTitleHeight(h1);
					});
					if (this.DOM.desc) {
						lockHeroBlockHeight(this.DOM.desc);
						setHeroIntroHiddenLayout(this.DOM.desc);
					}
				}
			} else {
				this.DOM.desc =
					this.$header.querySelector(".c-header__description, .c-header-simple__description") || null;
				this.DOM.btns = Array.from(
					this.$header.querySelectorAll(".c-header__actions .c-button, .c-header-simple__actions .c-button")
				);
				this.DOM.surtitle = this.$header.querySelector(".c-header__surtitle, .c-header-simple__surtitle") || null;
				this.DOM.actionsEl =
					this.$header.querySelector(".c-header__actions, .c-header-simple__actions") || null;
				/* Media hero : shell radius (pas img) — même intro home & intérieurs */
				const mediaShell = this.$header.querySelector(".c-header__background");
				const hasMedia = !!mediaShell?.querySelector?.("img, video");
				this._fullscreenHero = !!this.$header.classList?.contains?.("is-fullscreen");
				this.DOM.mediaShell = hasMedia ? mediaShell : null;
				this.DOM.mediaIntroTargets = hasMedia ? collectHeroMediaIntroTargets(mediaShell) : [];

				if (!this._introDone && !this._introStarted) {
					lockHeaderFootprint(this.$header);
					this.DOM.titlesSt1.forEach((h1) => {
						h1.classList.add("is-reveal-pending");
						h1.style.visibility = "hidden";
						lockHeroTitleHeight(h1);
					});
					const copyBlocks = [this.DOM.surtitle, this.DOM.desc, this.DOM.actionsEl].filter(Boolean);
					copyBlocks.forEach((el) => {
						lockHeroBlockHeight(el);
						setHeroIntroHiddenLayout(el);
					});
					/* Sous le rideau Barba : ne pas armer tout de suite (sinon reset + 2e wipe à l’intro) */
					const transitioning = document.documentElement.classList.contains("is-transitioning");
					/* Home plein écran : jamais de voile (le rideau 3 bandes suffit) — image
					 * pré-zoomée sous le loader / le rideau, dézoomée à l'ouverture */
					if (hasMedia && this._fullscreenHero) {
						this._armHeroZoom(mediaShell);
					} else if (hasMedia && !transitioning) {
						armHeroMediaShell(mediaShell, this._mobile, { fullscreen: this._fullscreenHero });
					}
				}
			}
		} else {
			this.DOM.desc = null;
			this.DOM.btns = [];
			this.DOM.surtitle = null;
			this.DOM.actionsEl = null;
			this.DOM.mediaShell = null;
			this.DOM.mediaIntroTargets = [];
		}
	}

	showAllImmediate() {
		this._introTl?.kill?.();
		this._heroMediaTl?.kill?.();
		this.DOM.titlesSt1?.forEach((h1) => {
			h1.classList.remove("is-reveal-pending");
			h1.style.visibility = "visible";
			this._resetSplitInners(h1);
		});
		this.DOM.titlesSt2?.forEach((t) => {
			t.classList.remove("is-reveal-pending");
			gsap.set(t, { clearProps: "all", visibility: "visible", autoAlpha: 1 });
			this._resetSplitInners(t);
		});
		this.DOM.revealBlocks?.forEach((el) => {
			el.__revealed = true;
			el.__revealStarted = true;
			gsap.set(el, { autoAlpha: 1, y: 0, clearProps: "transform" });
			queryOwned(el, LINES_SEL + ", h1").forEach((t) => {
				t.classList.remove("is-reveal-pending");
				t.style.visibility = "visible";
				this._resetSplitInners(t);
				this._revertTitleSplit(t);
			});
			queryOwned(el, "[data-reveal-item], [data-reveal-soft], [data-reveal-decor]").forEach((n) => {
				if (revealFadeOnly(n)) {
					gsap.set(n, { autoAlpha: 1 });
					return;
				}
				gsap.set(n, { autoAlpha: 1, y: 0, clearProps: "transform" });
			});
		if (isMeetReveal(el)) {
			const { left, right } = collectMeetPanes(el);
			left.forEach((n) => {
				const isDeco = n.classList?.contains?.("c-steps__deco");
				gsap.set(n, {
					autoAlpha: 1,
					visibility: "visible",
					y: 0,
					zIndex: isDeco ? 2 : 3,
					clearProps: "transform",
				});
			});
			right.forEach((n) => {
				gsap.set(n, { autoAlpha: 1, visibility: "visible", y: 0, zIndex: 1, clearProps: "transform" });
			});
		}
		if (isSliderReveal(el) || isTextImagePanelReveal(el)) {
			collectPanelSlideTargets(el).forEach((n) => {
				gsap.set(n, { autoAlpha: 1, y: 0, clearProps: "transform" });
			});
		}
	});
		if (this.$header) {
			finalizeHeroIntroLayout(this.DOM.titlesSt1, [
				this.DOM.surtitle,
				this.DOM.desc,
				this.DOM.actionsEl,
			]);
		} else {
			if (this.DOM.surtitle) gsap.set(this.DOM.surtitle, { opacity: 1, y: 0, clearProps: "transform" });
			if (this.DOM.desc) gsap.set(this.DOM.desc, { opacity: 1, y: 0, clearProps: "transform" });
			if (this.DOM.actionsEl) {
				gsap.set(this.DOM.actionsEl, { opacity: 1, y: 0, clearProps: "transform" });
			} else if (this.DOM.btns?.length) {
				gsap.set(this.DOM.btns, { opacity: 1, y: 0, clearProps: "transform" });
			}
			this.DOM.titlesSt1?.forEach((h1) => lockHeroTitleHeight(h1));
		}
		if (this.DOM.mediaShell) {
			settleHeroMediaShell(this.DOM.mediaShell, this.DOM.mediaIntroTargets, {
				fullscreen: !!this._fullscreenHero,
			});
		}
		if (this.DOM.mediaIntroTargets?.length) {
			gsap.set(this.DOM.mediaIntroTargets, { opacity: 1, y: 0, clearProps: "transform" });
		}
		this.$el.querySelectorAll(".split-line__inner, .split-word__inner").forEach((n) => {
			gsap.set(n, { yPercent: 0, y: 0, clearProps: "transform" });
		});
		if (this.$header) releaseHeaderFootprint(this.$header);
		this._introDone = true;
		this._markHeaderIntroDone();
	}

	_markHeaderIntroPlaying() {
		if (!this.$header) return;
		this.$header.classList.add("is-intro-playing");
		this.$header.classList.remove("is-intro-done");
	}

	_markHeaderIntroDone() {
		if (!this.$header) return;
		this.$header.classList.remove("is-intro-playing");
		this.$header.classList.add("is-intro-done");
	}

	_resetSplitInners(title) {
		if (!title) return;
		title.querySelectorAll(".split-line__inner, .split-word__inner").forEach((n) => {
			gsap.set(n, { yPercent: 0, y: 0, clearProps: "transform" });
		});
	}

	_prepareSoftTitle(title) {
		title.__useLineMask = false;
		title.__inners = [];
		title.__split?.revert?.();
		title.__split = null;
		title.classList.add("is-reveal-pending");
		gsap.set(title, {
			visibility: "visible",
			autoAlpha: 0,
			y: motionDistance("softY", this._mobile),
		});
	}

	async _collectTitleTweens(target) {
		const titles = queryOwned(target, LINES_SEL);
		const titleInners = [];
		const softTitles = [];
		if (!titles.length) return { titleInners, softTitles };
		await (this._assetsReady || preloadRevealAssets());
		if (this._destroyed) return { titleInners, softTitles };
		let SplitType = null;
		for (const title of titles) {
			if (!shouldUseLineMask(title, { mobile: this._mobile })) {
				this._prepareSoftTitle(title);
				title.style.visibility = "visible";
				title.classList.remove("is-reveal-pending");
				softTitles.push(title);
				continue;
			}
			SplitType ||= await getSplitType();
			if (!SplitType) {
				this._prepareSoftTitle(title);
				title.style.visibility = "visible";
				title.classList.remove("is-reveal-pending");
				softTitles.push(title);
				continue;
			}
			const inners = await this._splitTitleLines(title, SplitType);
			title.style.visibility = "visible";
			title.classList.remove("is-reveal-pending");
			titleInners.push(inners);
		}
		return { titleInners, softTitles };
	}

	_addTitleTweens(tl, titleInners, softTitles, position = 0) {
		titleInners.forEach((inners) => {
			if (!inners?.length) return;
			tl.to(
				inners,
				titleLinesTween(inners, this._mobile, () => this._revertTitleSplitIdle(inners.__title)),
				position
			);
		});
		if (softTitles.length) {
			tl.to(
				softTitles,
				{
					autoAlpha: 1,
					y: 0,
					duration: motionDuration("soft", this._mobile),
					ease: MOTION.ease.soft,
				},
				position
			);
		}
	}

	_maybeDeferToLoader() {
		const runAfterTransitionIfAny = () => {
			if (this._destroyed || this._started) return;
			this._started = true;
			if (this._loaderGuard) {
				clearTimeout(this._loaderGuard);
				this._loaderGuard = null;
			}

			const run = async () => {
				if (this._destroyed) return;
				await (this._assetsReady || preloadRevealAssets());
				if (this._destroyed) return;

				const fromTransition =
					document.documentElement.classList.contains("is-transitioning") ||
					window.__drieuxTransitionReveal === true;
				/*
				 * Le texte hero attend que le rideau soit vraiment ouvert (le média,
				 * lui, s'ouvre tout de suite). Loader intact : délai côté Reveal.
				 */
				const hi = MOTION.heroIntro || {};
				const lag = fromTransition
					? (hi.afterTransition ?? 0)
					: this._loaderWasPlaying
						? (hi.afterLoader ?? 0)
						: 0;
				const isPageIntro =
					!!this.$header ||
					!!this.$el?.querySelector?.(".c-contact__hero-title, .c-articles-list__header__title");
				if (lag > 0 && isPageIntro) {
					if (this.$header) this._playHeroMediaIntro();
					await new Promise((r) => setTimeout(r, Math.ceil(lag * 1000)));
					if (this._destroyed) return;
				}

				/*
				 * Hero d’abord (texte). Les autres instances Reveal attendent
				 * la fin du bloc texte — sinon profile-bar / four-push partent en même temps.
				 */
				if (this.$header) {
					await this._start();
				} else {
					await waitHeroTextDone(this);
				}
				/* Porte ouverte : les flush (scroll / enable-scroll / IO) peuvent révéler */
				this._gateOpen = true;

				await this.prepareOrphanTitles();
				this._startObserving();
				this._flushVisibleBlocks();
				this._flushVisibleOrphans();
				/* Post-Barba : 2e flush après layout (rideau / Lenis) */
				if (fromTransition) {
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							if (this._destroyed) return;
							this._flushVisibleBlocks();
							this._flushVisibleOrphans();
						});
					});
				}
				this._armSafetyFlush();
			};

			const html = document.documentElement;
			const transitioning = html.classList.contains("is-transitioning");
			const transitionReady =
				window.__drieuxTransitionReveal === true || html.classList.contains("is-transition-done");

			if (transitioning && !transitionReady) {
				let kicked = false;
				const kick = () => {
					if (this._destroyed || kicked) return;
					kicked = true;
					if (this._onTransitionReveal) {
						window.removeEventListener("page-transition:reveal", this._onTransitionReveal);
						this._onTransitionReveal = null;
					}
					if (this._onTransitionDone) {
						window.removeEventListener("page-transition:done", this._onTransitionDone);
						this._onTransitionDone = null;
					}
					run();
				};
				this._onTransitionReveal = kick;
				/*
				 * Filet : `_started` est déjà true pendant l’attente —
				 * tester `kicked`, sinon done ne relance jamais si reveal a raté.
				 */
				this._onTransitionDone = () => {
					if (!kicked) kick();
				};
				window.addEventListener("page-transition:reveal", this._onTransitionReveal, { once: true });
				window.addEventListener("page-transition:done", this._onTransitionDone, { once: true });

				/* TOCTOU — reveal déjà passé pendant le bind */
				if (window.__drieuxTransitionReveal || !html.classList.contains("is-transitioning")) {
					kick();
				}
			} else {
				run();
			}
		};

		const go = () => {
			runAfterTransitionIfAny();
			this._bindScrollFlush();
		};

		const loader = document.querySelector(".c-site-loader");
		const loaderDone =
			window.__drieuxLoaderDone === true || !!loader?.classList?.contains?.("loaded");
		const revealEarly = window.__drieuxLoaderReveal === true;
		/* Loader réellement joué (1re visite de session) → l'intro hero attend son ouverture */
		this._loaderWasPlaying = !!loader && !loaderDone && !revealEarly;

		if (loader && !loaderDone && !revealEarly) {
			this._onLoaderReveal = () => go();
			this._onLoaderDone = () => go();
			window.addEventListener("site-loader:reveal", this._onLoaderReveal, { once: true });
			window.addEventListener("site-loader:done", this._onLoaderDone, { once: true });

			/* TOCTOU */
			if (window.__drieuxLoaderReveal || window.__drieuxLoaderDone || loader.classList.contains("loaded")) {
				window.removeEventListener("site-loader:reveal", this._onLoaderReveal);
				window.removeEventListener("site-loader:done", this._onLoaderDone);
				this._onLoaderReveal = null;
				this._onLoaderDone = null;
				go();
			} else {
				setTimeout(() => {
					if (!this._destroyed) this._bindScrollFlush();
				}, 400);
				/* Filet : events jamais reçus */
				this._loaderGuard = setTimeout(() => {
					if (this._destroyed || this._started) return;
					go();
				}, 4000);
			}
		} else {
			go();
		}

		this._onEnableScroll = () => {
			if (this._destroyed || !this._started) return;
			this._flushVisibleBlocks();
			this._flushVisibleOrphans();
		};
		/* Bus app (EventEmitter) — pas window : menu/popin/PT émettent via app.emit */
		this.app?.on?.("enable-scroll", this._onEnableScroll);
	}

	_bindScrollFlush() {
		if (this._scrollFlushBound || this._destroyed) return;
		this._scrollFlushBound = true;
		const onScroll = () => {
			if (this._scrollFlushRaf) return;
			/* Filet : 1 passe max / 160ms (lectures de layout hors du chemin du scroll) */
			const now = performance.now();
			if (now - (this._lastScrollFlush || 0) < 160) return;
			this._lastScrollFlush = now;
			this._scrollFlushRaf = requestAnimationFrame(() => {
				this._scrollFlushRaf = null;
				if (this._destroyed || !this._started) return;
				this._flushVisibleBlocks();
				this._flushVisibleOrphans();
			});
		};
		/* Bus app (Lenis) + window natif — filet si Lenis ne relaye pas (scrollTo / trackpad) */
		this.app?.on?.("scroll", onScroll);
		this._onAppFlush = onScroll;
		window.addEventListener("scroll", onScroll, { passive: true });
		this._onWinFlush = onScroll;
	}

	/* --------------------------- hero intro (line-mask) --------------------------- */
	/**
	 * Wipe média hero — lancé dès l’ouverture rideau (loader / Barba),
	 * sans attendre SplitType (sinon overlay granit coincé pendant le chunk).
	 * 1 wipe max par navToken (page-transition incrémente le token).
	 */
	/** Home plein écran : image (img / vidéo) agrandie, invisible car sous le loader / rideau. */
	_armHeroZoom(mediaShell) {
		if (!mediaShell || prefersReducedMotion() || this._reduced) return;
		const bits = mediaShell.querySelectorAll("img, video");
		if (!bits.length) return;
		gsap.set(bits, {
			scale: MOTION.heroIntro?.mediaZoomFrom ?? 1.12,
			transformOrigin: "50% 50%",
			force3D: true,
		});
	}

	/** Home plein écran : dézoom lent à l'ouverture (loader ou transition) — une seule fois. */
	_playHeroZoom(mediaShell, hosts, navToken) {
		if (mediaShell.__heroZoomToken === navToken) return;
		mediaShell.__heroZoomToken = navToken;
		window.__drieuxHeroMediaDoneToken = navToken;
		/* Aucun voile ni zoom media-reveal ne doit s'y ajouter */
		hosts.forEach((h) => {
			h.__mediaRevealPlayed = true;
			h.__mediaRevealPlaying = false;
		});
		const bits = Array.from(mediaShell.querySelectorAll("img, video"));
		if (!bits.length) return;
		const hi = MOTION.heroIntro || {};
		mediaShell.__zooming = true;
		this._heroMediaTl?.kill?.();
		this._heroMediaTl = gsap.fromTo(
			bits,
			{ scale: hi.mediaZoomFrom ?? 1.12, transformOrigin: "50% 50%" },
			{
				scale: 1,
				duration: hi.mediaZoomDur ?? 2.2,
				ease: MOTION.ease.mediaScale || "expo.out",
				force3D: true,
				onComplete: () => {
					mediaShell.__zooming = false;
					/* Tactile : on garde le transform final (évite le re-rendu iOS) */
					if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
						gsap.set(bits, { clearProps: "transform" });
					}
				},
			}
		);
	}

	_playHeroMediaIntro() {
		const mediaShell = this.DOM.mediaShell;
		if (!mediaShell || this._destroyed) return;

		const fullscreen = !!this._fullscreenHero;
		const mediaIntroTargets = this.DOM.mediaIntroTargets?.length
			? this.DOM.mediaIntroTargets
			: collectHeroMediaIntroTargets(mediaShell);
		const navToken = window.__drieuxHeroMediaToken || 0;
		const hosts = Array.from(mediaShell.querySelectorAll?.("[data-reveal-media]") || []);
		const allPlayed = hosts.length
			? hosts.every((h) => h.__mediaRevealPlayed)
			: window.__drieuxHeroMediaDoneToken === navToken;

		/* Home plein écran : pas de voile, un dézoom (sauf reduced motion → réglé net) */
		if (fullscreen && !prefersReducedMotion() && !this._reduced) {
			this._playHeroZoom(mediaShell, hosts, navToken);
			return;
		}

		if (window.__drieuxHeroMediaDoneToken === navToken && allPlayed) {
			settleHeroMediaShell(mediaShell, mediaIntroTargets, { fullscreen });
			return;
		}

		/*
		 * Déjà en cours pour cette navigation (lancé pendant l'ouverture du loader, puis
		 * rappelé par _start) : ne JAMAIS relancer — sinon le voile est réarmé plein cadre
		 * et l'image « revient à son état initial et remonte » (double apparition home).
		 */
		const inFlight =
			hosts.some((h) => h.__mediaRevealPlaying) || !!this._heroMediaTl?.isActive?.();
		if (window.__drieuxHeroMediaDoneToken === navToken && inFlight) return;

		window.__drieuxHeroMediaDoneToken = navToken;
		this._heroMediaTl?.kill?.();

		/* Reduced motion, ou home plein écran (rideau 3 bandes seul, pas de voile en plus) */
		if (prefersReducedMotion() || this._reduced || fullscreen) {
			settleHeroMediaShell(mediaShell, mediaIntroTargets, { fullscreen });
			return;
		}

		armHeroMediaShell(mediaShell, this._mobile, {
			fullscreen,
			force: true,
		});

		const tl = gsap.timeline({
			onComplete: () => {
				settleHeroMediaShell(mediaShell, mediaIntroTargets, { fullscreen });
			},
		});
		this._heroMediaTl = tl;

		/* Wipe overlay only — aucun translate du shell (home + master). */
		if (mediaIntroTargets.length) {
			gsap.set(mediaIntroTargets, {
				y: 0,
				opacity: 1,
				visibility: "visible",
				clearProps: "transform",
			});
		}

		if (hosts.length) {
			hosts.forEach((host) => {
				host.__mediaRevealPlayed = false;
				host.__mediaRevealPlaying = false;
				host.__mediaRevealPrepared = false;
				prepareMediaRevealNode(host, this._mobile, { force: true });
				tweenMediaReveal(tl, host, this._mobile, 0);
			});
		} else {
			const bits = mediaShell.querySelectorAll?.("img, video");
			if (bits?.length) {
				gsap.set(bits, { scale: 1, clearProps: "transform" });
			}
		}
	}

	/**
	 * Intro post-loader / post-Barba : wipe media immédiat, puis titre + copy.
	 * Shell = `.c-header__background` (radius) — jamais l’img.
	 * Hauteur header figée avant SplitType pour tuer le CLS.
	 */
	async _start() {
		if (!this.$header) return;
		if (!this.DOM.titlesSt1.length) {
			window.__drieuxHeroTextDone = true;
			this._playHeroMediaIntro();
			try {
				window.dispatchEvent(new CustomEvent("hero-intro:text-done"));
			} catch {
				/* ignore */
			}
			return;
		}

		if (this._introDone) {
			this.showAllImmediate();
			this._bindResizeIfNeeded();
			return;
		}
		if (this._introStarted) {
			if (this._introTextDone) return;
			await new Promise((r) => {
				const id = setInterval(() => {
					if (this._introTextDone || this._introDone || this._destroyed) {
						clearInterval(id);
						r();
					}
				}, 40);
			});
			return;
		}
		this._introStarted = true;
		this._introTextDone = false;
		window.__drieuxHeroTextDone = false;

		/* Media dès le kick rideau — avant fonts / SplitType */
		this._playHeroMediaIntro();

		await (this._assetsReady || preloadRevealAssets());
		await waitFonts();
		if (this._destroyed) return;
		[this.DOM.surtitle, this.DOM.desc, this.DOM.actionsEl].filter(Boolean).forEach(lockHeroBlockHeight);
		this._introTl?.kill?.();

		const mediaShell = this.DOM.mediaShell;
		const mediaIntroTargets = this.DOM.mediaIntroTargets?.length
			? this.DOM.mediaIntroTargets
			: collectHeroMediaIntroTargets(mediaShell);

		/* Hauteur figée AVANT SplitType (revert/split gonfle le titre sinon) */
		lockHeaderFootprint(this.$header, { remount: true });

		let SplitType = null;
		const titleTweens = [];
		for (const h1 of this.DOM.titlesSt1) {
			unwrapSingleParagraph(h1);
			lockHeroTitleHeight(h1);
			h1.classList.add("is-reveal-pending");
			h1.style.visibility = "hidden";

			if (shouldUseLineMask(h1, { mobile: this._mobile })) {
				SplitType ||= await getSplitType();
				if (this._destroyed) return;
				h1._splitInstance?.revert?.();
				const { split, inners } = splitTitleIntoLines(h1, SplitType);
				h1._splitInstance = split;
				this._splits.push(split);
				lockHeroTitleHeight(h1);
				if (inners.length) {
					titleTweens.push({ type: "words", target: inners, title: h1 });
				} else {
					setHeroIntroHiddenLayout(h1);
					titleTweens.push({ type: "soft", target: h1, title: h1 });
				}
			} else {
				setHeroIntroHiddenLayout(h1);
				titleTweens.push({ type: "soft", target: h1, title: h1 });
			}
		}

		/*
		 * Surtitre + chapô hero : même masque ligne que le titre (cascade éditoriale).
		 * CTA : simple fondu montant. Géométrie identique (clip-path) → zéro CLS.
		 */
		const heroLines = [];
		if (shouldUseLineMask(this.$header, { mobile: this._mobile })) {
			SplitType ||= await getSplitType().catch(() => null);
			if (this._destroyed) return;
			if (SplitType) {
				const hi = MOTION.heroIntro || {};
				const sur = this.DOM.surtitle;
				if (sur && (sur.textContent || "").trim()) {
					const { split, inners } = splitTitleIntoLines(sur, SplitType);
					this._splits.push(split);
					if (inners.length) heroLines.push({ el: sur, inners, at: 0 });
				}
				const desc = this.DOM.desc;
				const descLen = (desc?.textContent || "").trim().length;
				if (desc && descLen && descLen <= LEAD_MAX_CHARS) {
					const paras = Array.from(desc.querySelectorAll(":scope > p"));
					(paras.length ? paras : [desc]).forEach((p, i) => {
						const { split, inners } = splitTitleIntoLines(p, SplitType);
						this._splits.push(split);
						if (inners.length) heroLines.push({ el: p, inners, at: (hi.leadAt ?? 0.45) + i * 0.1 });
					});
				}
			}
		}

		/* Remesure post-split (titres encore hidden) — un seul lock, zéro CLS visible */
		lockHeaderFootprint(this.$header, { remount: true });
		titleTweens.forEach((job) => {
			if (job.type === "words" && job.title) {
				job.title.style.visibility = "visible";
				job.title.classList.remove("is-reveal-pending");
			}
		});

		const copyBlocks = [this.DOM.surtitle, this.DOM.desc, this.DOM.actionsEl].filter(Boolean);
		const lineHosts = new Set(heroLines.map((j) => j.el));
		const splitCopy = copyBlocks.filter(
			(b) => lineHosts.has(b) || heroLines.some((j) => b.contains(j.el))
		);
		const fadeCopy = copyBlocks.filter((b) => !splitCopy.includes(b));
		if (splitCopy.length) gsap.set(splitCopy, { opacity: 1, visibility: "visible" });
		if (fadeCopy.length) {
			gsap.set(fadeCopy, { opacity: 0, visibility: "visible" });
		}

		this._markHeaderIntroPlaying();

		return new Promise((resolve) => {
			let textReleased = false;
			const releaseText = () => {
				if (textReleased) return;
				textReleased = true;
				this._introTextDone = true;
				window.__drieuxHeroTextDone = true;
				try {
					window.dispatchEvent(new CustomEvent("hero-intro:text-done"));
				} catch {
					/* ignore */
				}
				this.app?.emit?.("hero-intro:text-done");
				resolve();
			};

			const settleMediaShell = () =>
				settleHeroMediaShell(mediaShell, mediaIntroTargets, {
					fullscreen: !!this._fullscreenHero,
				});

			const tl = gsap.timeline({
				defaults: { ease: MOTION.ease.title },
				onComplete: () => {
					this._introSafety?.kill?.();
					this._introSafety = null;
					this._introDone = true;
					this._introTextDone = true;
					finalizeHeroIntroLayout(this.DOM.titlesSt1, copyBlocks);
					this.DOM.titlesSt1.forEach((h1) => this._revertTitleSplit(h1));
					heroLines.forEach((j) => this._revertTitleSplit(j.el));
					settleMediaShell();
					releaseHeaderFootprint(this.$header);
					this._markHeaderIntroDone();
					releaseText();
				},
			});
			this._introTl = tl;

			const hi = MOTION.heroIntro || {};
			const titleAt = heroLines.some((j) => j.at === 0) ? (hi.titleAt ?? 0.1) : 0;

			heroLines.forEach((j) => {
				tl.to(
					j.inners,
					{
						yPercent: 0,
						duration: motionDuration("introTitle", this._mobile),
						stagger: motionStagger("lines", this._mobile) ?? 0.1,
						ease: MOTION.ease.title,
						force3D: true,
					},
					j.at === 0 ? 0 : j.at + titleAt
				);
			});

			titleTweens.forEach((job, idx) => {
				if (job.type === "words") {
					tl.to(
						job.target,
						{
							yPercent: 0,
							duration: motionDuration("introTitle", this._mobile),
							stagger: motionStagger("lines", this._mobile) ?? 0.08,
							ease: MOTION.ease.title,
							force3D: true,
						},
						idx === 0 ? titleAt : "-=0.9"
					);
				} else {
					tl.to(
						job.target,
						{
							opacity: 1,
							y: 0,
							visibility: "visible",
							duration: motionDuration("introTitle", this._mobile),
							ease: MOTION.ease.title,
						},
						idx === 0 ? 0 : "-=0.28"
					);
				}
			});

			if (fadeCopy.length) {
				const fadeAt = (hi.actionsAt ?? 0.7) + titleAt;
				fadeCopy.forEach((b, i) => {
					tl.fromTo(
						b,
						{ opacity: 0, y: 12 },
						{
							opacity: 1,
							y: 0,
							duration: motionDuration("introSoft", this._mobile),
							ease: MOTION.ease.soft,
							clearProps: "transform",
						},
						b === this.DOM.actionsEl ? fadeAt : (hi.leadAt ?? 0.45) + titleAt + i * 0.08
					);
				});
			}

			tl.addLabel("titleEnd");
			/* Media : timeline dédiée _playHeroMediaIntro (pas de 2e wipe ici) */

			/*
			 * Le reste de la page n'attend pas la toute fin des courbes expo (texte déjà
			 * posé visuellement à ~90 %) : sinon ~2 s d'attente avant les 1res cartes.
			 */
			tl.call(releaseText, null, Math.max(0.35, tl.duration() - 0.75));

			this._introSafety?.kill?.();
			this._introSafety = gsap.delayedCall(MOTION.safetyIntroSec, () => {
				if (this._destroyed || this._introDone) return;
				this._introTl?.progress?.(1);
				this._heroMediaTl?.progress?.(1);
				this._introDone = true;
				this._introTextDone = true;
				finalizeHeroIntroLayout(this.DOM.titlesSt1, copyBlocks);
				this.DOM.titlesSt1.forEach((h1) => {
					h1.querySelectorAll(".split-line__inner, .split-word__inner").forEach((n) => {
						gsap.set(n, { yPercent: 0, clearProps: "transform" });
					});
					this._revertTitleSplit(h1);
				});
				heroLines.forEach((j) => this._revertTitleSplit(j.el));
				settleMediaShell();
				releaseHeaderFootprint(this.$header);
				this._markHeaderIntroDone();
				releaseText();
			});

			this._bindResizeIfNeeded();
		});
	}

	/* --------------------------- [data-reveal] sections --------------------------- */
	wireReveals() {
		if (!this.DOM.revealBlocks.length) return;
		/* Prépare (cache) tout de suite ; observe seulement après loader (_startObserving). */
		this.DOM.revealBlocks.forEach((el) => this._prepareBlock(el));
		flushHeaderAttachedNodes(this.$el);
	}

	_startObserving() {
		if (!this.DOM.revealBlocks?.length || this._ioBlocks) return;
		this._presplitPending();

		this._ioBlocks = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					const target = entry.target;
					if (target.__revealed) {
						this._ioBlocks.unobserve(target);
						return;
					}
					/* __revealStarted sans __revealed = en cours / coincé — laisser flush forcer */
					if (target.__revealStarted && !target.__revealed) return;
					/*
					 * rootMargin peut intersecter avant la bande utile.
					 * Rester observé — le prochain seuil / flush scroll lancera l’anim.
					 */
					if (!isInViewportBand(target, window.innerHeight || 0)) return;
					this.animateBlock(target);
					if (target.__revealed) this._ioBlocks.unobserve(target);
				});
			},
			{
				root: null,
				threshold: [0, 0.01, 0.08, 0.16, 0.28],
				rootMargin: motionIo("blocks", this._mobile).rootMargin || "0px 0px -5% 0px",
			}
		);

		this.DOM.revealBlocks.forEach((el) => {
			if (!el.__revealed && !el.__revealStarted) this._ioBlocks.observe(el);
		});

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				this._flushVisibleBlocks();
				this._flushVisibleOrphans();
			});
		});
	}

	_flushVisibleBlocks() {
		if (!this.DOM.revealBlocks?.length) return;
		const vh = window.innerHeight || 0;
		const now = Date.now();
		this.DOM.revealBlocks.forEach((el) => {
			if (el.__revealed) return;
			/*
			 * Reveal démarré mais pas terminé (await SplitType / timeline) :
			 * TOUJOURS rescuer après 700ms — même hors viewport.
			 * Sinon scroll rapide → titres visibility:hidden pour toujours.
			 */
			/* Reveal démarré mais bloqué vraiment longtemps (split/queue morte) */
			if (el.__revealStarted && !el.__revealed) {
				const startedAt = el.__revealStartedAt || 0;
				if (startedAt && now - startedAt > 2200) {
					this._forceShowBlock(el);
					this._ioBlocks?.unobserve?.(el);
				}
				return;
			}
			/* Pas encore démarré : uniquement si en bande viewport */
			if (!isInViewportBand(el, vh)) return;
			this.animateBlock(el);
			if (el.__revealed) this._ioBlocks?.unobserve?.(el);
		});
	}

	_flushVisibleOrphans() {
		if (!this._gateOpen || !this.DOM.orphanTitles?.length) return;
		const vh = window.innerHeight || 0;
		if (pageStillCovered()) return;
		this.DOM.orphanTitles.forEach((title) => {
			if (!title.classList.contains("is-reveal-pending")) return;
			if (title.__useLineMask !== false && !title.__inners?.length) return;
			if (!isInViewportBand(title, vh)) return;
			this.animateOrphanTitle(title);
			this._ioTitles?.unobserve?.(title);
		});
	}

	_prepareBlock(el) {
		if (el.__revealPrepared) return;
		el.__revealPrepared = true;

		/* Meet (steps) : panes entières — pas de soft/item/titre découplés */
		if (isMeetReveal(el)) {
			this._prepareMeetBlock(el);
			return;
		}

		/* Slider réalisations : panel entier depuis la droite */
		if (isSliderReveal(el)) {
			this._preparePanelSlideBlock(el);
			return;
		}

		/* Text-image thémé : fond + texte en slide horizontal (image fixe) */
		if (isTextImagePanelReveal(el)) {
			this._preparePanelSlideBlock(el);
			return;
		}

		/*
		 * Testimony : pas de hide reveal sur le shell / media.
		 * (data-reveal-item + parallax → autoAlpha:0 coincé, portrait invisible)
		 * Entrée slides = testimony.js ; parallax garde le transform.
		 */
		if (el.matches?.(".c-testimony, [data-component~='testimony']")) {
			this._prepareTestimonyBlock(el);
			return;
		}

		this._ensureRevealMarks(el);

		const titles = queryOwned(el, LINES_SEL);
		const soft = queryOwned(el, "[data-reveal-soft]");
		const items = queryOwned(el, "[data-reveal-item]");
		const decors = queryOwned(el, "[data-reveal-decor]");
		const isFooter = !!el.matches?.(".c-site-footer, [data-component~='site-footer']");
		const prepSoftY = isFooter
			? motionDistance("footerSoftY", this._mobile)
			: motionDistance("softY", this._mobile);
		const prepItemY = isFooter
			? motionDistance("footerY", this._mobile)
			: motionDistance("itemY", this._mobile);

		/* Enfant prose : le nœud lui-même est le soft (sauf titres line-mask) */
		const selfSoft = !!el.__revealSelf && !titles.length;

		titles.forEach((t) => {
			t.classList.add("is-reveal-pending");
			gsap.set(t, { visibility: "hidden" });
		});
		soft.forEach((n) => {
			if (isHeaderAttachedNode(n)) return;
			if (revealFadeOnly(n)) {
				gsap.set(n, { autoAlpha: 0 });
				return;
			}
			gsap.set(n, {
				autoAlpha: 0,
				y: prepSoftY,
			});
		});
		items.forEach((n) => {
			if (revealFadeOnly(n)) {
				gsap.set(n, { autoAlpha: 0 });
				return;
			}
			if (isRiseReveal(n)) {
				gsap.set(n, {
					autoAlpha: 1,
					visibility: "visible",
					yPercent: motionDistance("riseYPercent", this._mobile) ?? 100,
					y: 0,
				});
				return;
			}
			/* Card avec media reveal : pas d’autoAlpha 0 (cacherait le mask/scale) */
			if (n.querySelector?.("[data-reveal-media]")) {
				gsap.set(n, {
					autoAlpha: 1,
					visibility: "visible",
					y: prepItemY,
				});
				n.__cardParts = mediaCardParts(n);
				if (n.__cardParts.length) gsap.set(n.__cardParts, { autoAlpha: 0, y: prepSoftY });
				return;
			}
			gsap.set(n, {
				autoAlpha: 0,
				y: prepItemY,
			});
		});
		queryOwned(el, "[data-reveal-media]").forEach((n) => {
			prepareMediaRevealNode(n, this._mobile);
		});
		/* Agencies / cards : media parfois hors queryOwned nesting — scan direct */
		el.querySelectorAll?.("[data-reveal-media]")?.forEach((n) => {
			if (!n.__mediaReveal) prepareMediaRevealNode(n, this._mobile);
		});
		decors.forEach((n) => {
			if (revealFadeOnly(n)) {
				gsap.set(n, { autoAlpha: 0 });
				return;
			}
			gsap.set(n, {
				autoAlpha: 0,
				y: motionDistance("decorY", this._mobile),
			});
		});
		/* Masques existants (labels de liens footer) : le texte attend sous son masque.
		   transition:none = pas de conflit avec la transition CSS du hover. */
		queryOwned(el, "[data-reveal-mask]").forEach((n) => {
			gsap.set(n, { yPercent: 110, transition: "none", force3D: true });
		});

		if (selfSoft || (!titles.length && !soft.length && !items.length && !decors.length)) {
			/*
			 * Module sans marks : soft self. Testimony / slider déjà short-circuités plus haut.
			 */
			gsap.set(el, { autoAlpha: 0, y: prepSoftY });
			el.__revealSelf = true;
		}
	}

	_prepareTestimonyBlock(el) {
		/*
		 * Skip Reveal IO/timeline — entrée = testimony.js (quote → media → flèches).
		 * Ne PAS forcer autoAlpha:1 ici (sinon prepareEntrance de testimony est écrasé :
		 * data-component="testimony reveal" → testimony d’abord, reveal ensuite).
		 */
		el.__revealed = true;
		el.__revealStarted = true;
	}

	_prepareMeetBlock(el) {
		const { left, right } = collectMeetPanes(el);
		el.__meetLeft = left;
		el.__meetRight = right;
		el.__revealMeet = true;

		/* Panes décalés en Y dès le prepare — rise cabine */
		setMeetPaneOffsets(left, right, this._mobile);

		queryOwned(el, LINES_SEL).forEach((t) => {
			t.classList.add("is-reveal-pending");
			gsap.set(t, { visibility: "hidden" });
		});
	}

	_preparePanelSlideBlock(el) {
		const panels = collectPanelSlideTargets(el);
		el.__panelSlidePanels = panels;
		if (isSliderReveal(el)) el.__revealSlider = true;
		if (isTextImagePanelReveal(el)) el.__revealTextImagePanel = true;

		if (panels.length) {
			const y = panelSlideTravelY(this._mobile);
			gsap.set(panels, { autoAlpha: 0, visibility: "visible", y, force3D: true });
		}

		if (isTextImagePanelReveal(el)) {
			queryOwned(el, ".c-text-image__image").forEach((n) => {
				gsap.set(n, { autoAlpha: 1, visibility: "visible", y: 0, clearProps: "transform" });
			});
		}

		/* Slider + text-image panel : mask/scale sur [data-reveal-media] */
		const mediaHosts = [
			...new Set([
				...queryOwned(el, "[data-reveal-media]"),
				...Array.from(el.querySelectorAll?.("[data-reveal-media]") || []),
			]),
		];
		mediaHosts.forEach((n) => prepareMediaRevealNode(n, this._mobile));

		queryOwned(el, LINES_SEL).forEach((t) => {
			t.classList.add("is-reveal-pending");
			gsap.set(t, { visibility: "hidden" });
		});
	}

	_prepareSliderBlock(el) {
		this._preparePanelSlideBlock(el);
	}

	/**
	 * Filet : marque soft/item/st2 manquants dans un bloc [data-reveal]
	 * (modules oubliés en Twig → toujours un scroll reveal).
	 */
	/** Item sous l'écran au moment du reveal de son bloc : révélé à son arrivée. */
	_observeLateItem(item) {
		if (!item || item.__lateDone) return;
		if (!this._ioLate) {
			const io = motionIo("blocks", this._mobile);
			this._ioLate = new IntersectionObserver(
				(entries) => {
					const hits = entries.filter((e) => e.isIntersecting).map((e) => e.target);
					hits.forEach((t) => this._ioLate.unobserve(t));
					/* Cards arrivées ensemble sur une même ligne : cascade gauche → droite */
					const ranks = mediaRowRanks(hits);
					const step = motionStagger("mediaRow", this._mobile) ?? 0.12;
					hits.forEach((t) => this._revealLateItem(t, (ranks.get(t) || 0) * step));
				},
				{ root: null, threshold: 0, rootMargin: io.rootMargin || "0px 0px -10% 0px" }
			);
		}
		this._ioLate.observe(item);
	}

	_revealLateItem(item, delay = 0) {
		if (!item || item.__lateDone || this._destroyed) return;
		item.__lateDone = true;
		const mobile = this._mobile;
		const tl = gsap.timeline({ delay });
		if (isRiseReveal(item)) {
			tl.to(item, { yPercent: 0, duration: motionDuration("block", mobile), ease: MOTION.ease.block }, 0);
		} else if (revealFadeOnly(item)) {
			tl.to(item, { autoAlpha: 1, duration: motionDuration("item", mobile), ease: MOTION.ease.block }, 0);
		} else {
			tl.to(item, { autoAlpha: 1, y: 0, duration: motionDuration("item", mobile), ease: MOTION.ease.block }, 0);
		}
		item.querySelectorAll("[data-reveal-media]").forEach((host) => {
			if (host.__mediaRevealPlayed || host.__mediaRevealPlaying) return;
			tweenMediaReveal(tl, host, mobile, 0.05);
		});
		let k = 0;
		item.querySelectorAll(LINES_SEL).forEach((t) => {
			t.classList.remove("is-reveal-pending");
			t.style.visibility = "visible";
			gsap.set(t, { autoAlpha: 1, visibility: "visible" });
			const inners = t.__inners?.length && t.__inners[0]?.isConnected ? t.__inners : null;
			if (!inners) return;
			tl.to(inners, titleLinesTween(inners, mobile, () => this._revertTitleSplitIdle(t)), 0.15 + k * 0.12);
			k += 1;
		});
		item.querySelectorAll("[data-reveal-mask]").forEach((m, i) => {
			tl.to(
				m,
				{
					yPercent: 0,
					duration: motionDuration("title", mobile),
					ease: MOTION.ease.title,
					onComplete: () => gsap.set(m, { clearProps: "transform,transition" }),
				},
				0.12 + i * 0.045
			);
		});		this._tweenCardParts(tl, item, 0.15);
	}

	/** Textes d'une card media : fondu montant en cascade, à `at` (s) dans le timeline. */
	_tweenCardParts(tl, item, at) {
		const parts = item?.__cardParts;
		if (!parts?.length) return;
		tl.to(
			parts,
			{
				autoAlpha: 1,
				y: 0,
				/* Plus court que les blocs : petits textes de card, ils suivent l'image sans traîner */
				duration: (motionDuration("soft", this._mobile) ?? 1.2) * 0.75,
				stagger: 0.06,
				ease: MOTION.ease.soft,
				onComplete: () => settleMediaCardParts(item),
			},
			at
		);
	}

	_ensureRevealMarks(el) {
		if (!el?.querySelectorAll) return;
		/* Meet / slider / text-image panel : ne pas marquer soft/item (évite texte avant fond) */
		if (isMeetReveal(el) || isPanelSlideReveal(el)) return;

		el.querySelectorAll("h2:not(.st1):not(.st2)").forEach((h) => {
			if (h.closest("[data-reveal-item]")) return;
			h.classList.add("st2");
		});

		/* Surtitres : petite ligne qui glisse sous son masque (comme les titres) */
		el.querySelectorAll(".surtitle").forEach((n) => {
			if (n.closest("[data-reveal-item]")) return;
			if (n.closest(".c-header, .c-header-simple")) return;
			n.removeAttribute("data-reveal-soft");
			n.removeAttribute("data-reveal-soft");
			n.setAttribute("data-reveal-lines", "");
		});

		/* Chapôs courts : lignes ; longs : fondu (ne pas surcharger) */
		el.querySelectorAll(LEAD_LINES_SELECTOR).forEach((lead) => {
			if (lead.closest("[data-reveal-item]")) return;
			const paras = Array.from(lead.querySelectorAll(":scope > p"));
			const total = (lead.textContent || "").trim().length;
			if (!total) return;
			if (total > LEAD_MAX_CHARS) {
				if (!lead.closest("[data-reveal-soft]")) lead.setAttribute("data-reveal-soft", "");
				return;
			}
			lead.removeAttribute("data-reveal-soft");
			if (paras.length) {
				paras.forEach((p) => p.setAttribute("data-reveal-lines", ""));
			} else {
				lead.setAttribute("data-reveal-lines", "");
			}
		});

		el.querySelectorAll(".c-button").forEach((btn) => {
			if (btn.closest("[data-reveal-soft], [data-reveal-item]")) return;
			/* Testimony : entrée gérée par testimony.js (éviter softY vs creative/parallax) */
			if (btn.closest(".c-testimony")) return;
			/* Push / text-image CTA : jamais soft — autoAlpha:0 coincé = pastilles vides */
			if (btn.closest(".c-push-crossnav, .c-text-image")) return;
			btn.setAttribute("data-reveal-soft", "");
		});

		/* Titres de cards : lignes (calées sur l'arrivée de leur card) */
		el.querySelectorAll(".c-four-push__item__title, .c-three-push__item__title").forEach((n) => {
			n.setAttribute("data-reveal-lines", "");
		});
		/* Texte des engagements (three-push) : lignes si court, sinon fondu */
		el.querySelectorAll(".c-three-push__item__content").forEach((n) => {
			const len = (n.textContent || "").trim().length;
			if (!len) return;
			if (len > LEAD_MAX_CHARS) return;
			const paras = Array.from(n.querySelectorAll(":scope > p"));
			(paras.length ? paras : [n]).forEach((p) => p.setAttribute("data-reveal-lines", ""));
		});
		/* Footer : titres de colonnes en lignes + chaque lien glisse sous son masque */
		el.querySelectorAll(".c-site-footer__col .c-site-footer__col-title").forEach((n) => {
			n.setAttribute("data-reveal-lines", "");
		});
		el.querySelectorAll(".c-site-footer__col .c-site-footer__link-label__inner").forEach((n) => {
			n.setAttribute("data-reveal-mask", "");
		});

		const itemSelectors = [
			".c-four-push__item",
			".c-three-push__item",
			".c-article-card",
			".c-agencies__card",
			".c-numbers__item",
			".c-faq__item",
			".c-certifications__logos > li",
			".c-contact__phone-card",
			".c-contact__email-panel",
		].join(", ");

		el.querySelectorAll(itemSelectors).forEach((n) => {
			if (n.hasAttribute("data-reveal-item")) return;
			if (n.closest("[data-reveal-item]")) return;
			n.setAttribute("data-reveal-item", "");
		});

		const softSelectors = [
			".c-text-image__description",
			".c-four-push__subtitle",
			/* Pas push __desc / __actions : soft coincé = bandeau CTA illisible */
			".c-agencies__description",
			".c-numbers__description",
			".c-faq__intro",
			".c-simple-text__content",
			".c-contact__hero-text",
			".c-articles-list__header__description",
			".c-push-article__cta",
			".c-logos__content button, .c-logos .c-button",
		].join(", ");

		el.querySelectorAll(softSelectors).forEach((n) => {
			if (n.hasAttribute("data-reveal-soft") || n.hasAttribute("data-reveal-item")) return;
			if (n.closest("[data-reveal-soft], [data-reveal-item]")) return;
			n.setAttribute("data-reveal-soft", "");
		});
	}

	_scheduleRevealBlock(target) {
		if (!target || target.__revealed || target.__revealStarted || target.__revealQueued) return;
		target.__revealStarted = true;
		target.__revealStartedAt = Date.now();
		target.__revealQueued = true;

		const run = async () => {
			this._activeRevealCount += 1;
			try {
				await this._runRevealBlock(target);
			} finally {
				target.__revealQueued = false;
				this._activeRevealCount = Math.max(0, this._activeRevealCount - 1);
				this._drainRevealQueue();
			}
		};

		if (this._activeRevealCount < this._maxConcurrentReveals) {
			run();
			return;
		}

		this._revealQueue.push(run);
	}

	/** IO rootMargin peut déclencher trop tôt — on réarme au lieu de forceShow. */
	_rearmBlock(target) {
		if (!target || target.__revealed) return;
		target.__blockTl?.kill?.();
		target.__blockTl = null;
		target.__meetTl?.kill?.();
		target.__meetTl = null;
		target.__sliderTl?.kill?.();
		target.__sliderTl = null;
		this._clearBlockSafety(target);
		if (target.__revealMeet || isMeetReveal(target)) {
			setMeetPaneOffsets(target.__meetLeft || [], target.__meetRight || [], this._mobile);
			queryOwned(target, LINES_SEL).forEach((t) => {
				t.classList.add("is-reveal-pending");
				gsap.set(t, { visibility: "hidden" });
			});
		} else if (target.__revealSlider || isSliderReveal(target) || target.__revealTextImagePanel || isTextImagePanelReveal(target)) {
			const y = panelSlideTravelY(this._mobile);
			const panels = target.__panelSlidePanels || collectPanelSlideTargets(target);
			if (panels.length) {
				gsap.set(panels, { autoAlpha: 0, visibility: "visible", y, force3D: true });
			}
			queryOwned(target, LINES_SEL).forEach((t) => {
				t.classList.add("is-reveal-pending");
				gsap.set(t, { visibility: "hidden" });
			});
		}
		target.__revealStarted = false;
		target.__revealStartedAt = 0;
		target.__revealQueued = false;
		if (this._ioBlocks && !target.__revealed) {
			this._ioBlocks.observe(target);
		}
	}

	_clearBlockSafety(target) {
		if (!target) return;
		const id = target.__revealSafetyId;
		if (id) {
			clearTimeout(id);
			this._safetyTimeouts = (this._safetyTimeouts || []).filter((t) => t !== id);
			target.__revealSafetyId = null;
		}
		target.__revealSafetyArmed = false;
	}

	_drainRevealQueue() {
		while (this._activeRevealCount < this._maxConcurrentReveals && this._revealQueue.length) {
			const next = this._revealQueue.shift();
			next?.();
		}
	}

	async animateBlock(target) {
		/*
		 * _started passe à true AVANT l'attente du hero : sans porte, un flush de scroll
		 * (Lenis émet au démarrage) lançait four-push & co pendant l'intro du hero.
		 */
		if (!this._started || !this._gateOpen || this._destroyed) return;
		/* Page encore couverte (rideau / image partagée) : un flush de scroll ne doit
		 * jamais lancer un reveal invisible — run() re-flushe au signal de reveal */
		if (pageStillCovered()) return;
		this._scheduleRevealBlock(target);
	}

	_markBlockRevealed(target) {
		if (!target) return;
		this._clearBlockSafety(target);
		target.__revealed = true;
		target.__revealStarted = true;
		if (isTextImagePanelReveal(target)) {
			collectTextImagePanel(target).forEach((n) => {
				gsap.set(n, { autoAlpha: 1, visibility: "visible", y: 0, clearProps: "transform" });
			});
		}
	}

	async _runRevealBlock(target) {
		if (!target) return;

		const markDone = () => this._markBlockRevealed(target);

		try {
			if (target.__revealMeet || isMeetReveal(target)) {
				const vh = window.innerHeight || 0;
				if (isScrolledPast(target, vh)) {
					this._forceShowBlock(target);
					return;
				}
				await this._runMeetReveal(target, markDone);
				return;
			}

			if (target.__revealSlider || isSliderReveal(target) || target.__revealTextImagePanel || isTextImagePanelReveal(target)) {
				const vh = window.innerHeight || 0;
				if (isScrolledPast(target, vh)) {
					this._forceShowBlock(target);
					return;
				}
				await this._runPanelSlideReveal(target, markDone);
				return;
			}

			const delay = parseFloat(target.getAttribute("data-reveal-delay") || "0") || 0;
			const staggerAttr = parseFloat(target.getAttribute("data-reveal-stagger") || "") || 0;
			const isFooter = !!target.matches?.(".c-site-footer, [data-component~='site-footer']");
			const itemStagger =
				staggerAttr ||
				(isFooter ? motionStagger("footer", this._mobile) : motionStagger("items", this._mobile));
			const itemDur = isFooter
				? motionDuration("footer", this._mobile)
				: motionDuration("item", this._mobile);
			const softDur = isFooter
				? motionDuration("footerSoft", this._mobile)
				: motionDuration("soft", this._mobile);

			let titles = queryOwned(target, LINES_SEL);
			const soft = queryOwned(target, "[data-reveal-soft]").filter((n) => !isHeaderAttachedNode(n));
			let items = queryOwned(target, "[data-reveal-item]");
			const decors = queryOwned(target, "[data-reveal-decor]");
			let mediaNodes = [
				...new Set([
					...queryOwned(target, "[data-reveal-media]"),
					...Array.from(target.querySelectorAll?.("[data-reveal-media]") || []),
				]),
			];
			let masks = queryOwned(target, "[data-reveal-mask]");

			/*
			 * Longues listes (articles, cards) : les items encore SOUS l'écran ne
			 * partent pas avec le bloc — chacun se révèle à son arrivée (sinon on
			 * découvre des cartes déjà posées, « d'un coup »).
			 */
			if (!isFooter && items.length > 1) {
				const vhNow = window.innerHeight || 0;
				const bandPx = vhNow * (motionIo("blocks", this._mobile).bandTop ?? 0.85);
				const late = items.filter((n) => n.getBoundingClientRect().top > bandPx + 24);
				if (late.length && late.length < items.length) {
					const inLate = (node) => late.some((li) => li.contains(node));
					items = items.filter((n) => !late.includes(n));
					mediaNodes = mediaNodes.filter((m) => !inLate(m));
					titles = titles.filter((t) => !inLate(t));
					masks = masks.filter((m) => !inLate(m));
					late.forEach((n) => this._observeLateItem(n));
				}
			}

			let titleInners = [];
			let softTitles = [];
			if (titles.length) {
				try {
					await (this._assetsReady || preloadRevealAssets());
					if (this._destroyed) {
						this._forceShowBlock(target);
						return;
					}
					let SplitType = null;
					for (const title of titles) {
						if (!shouldUseLineMask(title, { mobile: this._mobile })) {
							this._prepareSoftTitle(title);
							title.style.visibility = "visible";
							title.classList.remove("is-reveal-pending");
							softTitles.push(title);
							continue;
						}
						SplitType ||= await getSplitType();
						if (!SplitType) {
							this._prepareSoftTitle(title);
							title.style.visibility = "visible";
							title.classList.remove("is-reveal-pending");
							softTitles.push(title);
							continue;
						}
						const inners = await this._splitTitleLines(title, SplitType);
						title.style.visibility = "visible";
						title.classList.remove("is-reveal-pending");
						titleInners.push(inners);
					}
				} catch {
					titles.forEach((title) => {
						title.style.visibility = "visible";
						title.classList.remove("is-reveal-pending");
						gsap.set(title, { autoAlpha: 1, y: 0, clearProps: "transform" });
						softTitles.push(title);
					});
				}
			}

			/*
			 * Après await SplitType : l’IO (rootMargin) peut avoir fire trop tôt,
			 * ou l’utilisateur a filé. Ne JAMAIS forceShow si encore sous le fold
			 * — sinon le module apparaît déjà posé quand on arrive (0 anim perçue).
			 */
			const vh = window.innerHeight || 0;
			if (isScrolledPast(target, vh)) {
				this._forceShowBlock(target);
				return;
			}
			if (!isInViewportBand(target, vh)) {
				/*
				 * IO rootMargin a fire trop tôt. Réarme SANS re-observe
				 * (sinon boucle tant que le root élargi intersecte).
				 * Le flush scroll relancera une fois dans la bande.
				 */
				this._rearmBlock(target);
				return;
			}

			const tl = gsap.timeline({
				delay,
				defaults: { ease: MOTION.ease.block },
				onComplete: markDone,
			});
			target.__blockTl = tl;
			this._armBlockSafety(target);

			if (target.__revealSelf) {
				tl.to(target, {
					autoAlpha: 1,
					y: 0,
					duration: motionDuration("block", this._mobile),
					ease: MOTION.ease.soft,
				});
			} else {
				/* Cascade dans l'ordre du DOM : surtitre → titre → chapô (0.12s) */
				let linesAt = 0;
				const itemOf = (node) => items.find((it) => it.contains(node)) || null;
				const itemLines = [];
				titleInners.forEach((inners) => {
					if (!inners?.length) return;
					if (itemOf(inners.__title)) {
						itemLines.push(inners);
						return;
					}
					tl.to(
						inners,
						titleLinesTween(inners, this._mobile, () => this._revertTitleSplitIdle(inners.__title)),
						linesAt
					);
					linesAt += 0.12 + Math.min(inners.length - 1, 3) * 0.05;
				});

				if (softTitles.length || soft.length) {
					const softFade = [...softTitles, ...soft].filter(revealFadeOnly);
					const softMove = [...softTitles, ...soft].filter((n) => !revealFadeOnly(n));
					const softAt = isFooter
						? 0.12
						: titleInners.length
							? 0.2
							: 0;
					if (softMove.length) {
						tl.to(
							softMove,
							{
								autoAlpha: 1,
								y: 0,
								duration: softDur,
								stagger: motionStagger("soft", this._mobile),
								ease: MOTION.ease.soft,
							},
							softAt
						);
					}
					if (softFade.length) {
						tl.to(
							softFade,
							{
								autoAlpha: 1,
								duration: softDur,
								stagger: motionStagger("soft", this._mobile),
								ease: MOTION.ease.soft,
							},
							softAt
						);
					}
				}

				if (items.length) {
					const itemFade = items.filter(revealFadeOnly);
					const itemMove = items.filter((n) => !revealFadeOnly(n));
					const itemRise = itemMove.filter(isRiseReveal);
					const itemCard = itemMove.filter((n) => !isRiseReveal(n));
					/* Cards tôt — chevauche soft/titre pour que l’arrivée en vue voie le rise */
					const itemAt = isFooter
						? 0
						: titleInners.length
							? 0.12
							: soft.length || softTitles.length
								? 0.3
								: 0;
					if (itemCard.length) {
						tl.to(
							itemCard,
							{
								autoAlpha: 1,
								y: 0,
								duration: itemDur,
								stagger: itemStagger,
								ease: isFooter ? MOTION.ease.soft : MOTION.ease.block,
							},
							itemAt
						);
					}
					if (itemRise.length) {
						tl.to(
							itemRise,
							{
								yPercent: 0,
								duration: motionDuration("block", this._mobile),
								stagger: itemStagger,
								ease: MOTION.ease.block,
							},
							itemAt
						);
					}
					if (itemFade.length) {
						tl.to(
							itemFade,
							{
								autoAlpha: 1,
								duration: itemDur,
								stagger: itemStagger,
								ease: isFooter ? MOTION.ease.soft : MOTION.ease.block,
							},
							itemAt
						);
					}
				}

				/* Lignes / masques DANS les cards : calés sur l'arrivée de leur card */
				{
					const itemAtNum = isFooter ? 0 : titleInners.length ? 0.12 : soft.length || softTitles.length ? 0.3 : 0;
					const itemStart = (node) => {
						const it = itemOf(node);
						const idx = it ? items.indexOf(it) : 0;
						return itemAtNum + Math.max(0, idx) * itemStagger;
					};
					itemLines.forEach((inners, k) => {
						const sameItemBefore = itemLines
							.slice(0, k)
							.filter((x) => itemOf(x.__title) === itemOf(inners.__title)).length;
						tl.to(
							inners,
							titleLinesTween(inners, this._mobile, () => this._revertTitleSplitIdle(inners.__title)),
							itemStart(inners.__title) + 0.15 + sameItemBefore * 0.12
						);
					});
					items.forEach((it) => {
						if (it.__cardParts?.length) this._tweenCardParts(tl, it, itemStart(it) + 0.18);
					});
					masks.forEach((m) => {
						const it = itemOf(m);
						const rank = it ? masks.filter((x) => itemOf(x) === it).indexOf(m) : masks.indexOf(m);
						tl.to(
							m,
							{
								yPercent: 0,
								duration: motionDuration("title", this._mobile),
								ease: MOTION.ease.title,
								force3D: true,
								onComplete: () => gsap.set(m, { clearProps: "transform,transition" }),
							},
							itemStart(m) + 0.12 + rank * 0.045
						);
					});
				}

				if (mediaNodes.length) {
					const mediaAt = titleInners.length ? 0.15 : soft.length || softTitles.length ? 0.35 : 0;
					const cardAt = isFooter ? 0 : titleInners.length ? 0.12 : soft.length || softTitles.length ? 0.3 : 0;
					const rowStagger = motionStagger("mediaRow", this._mobile) ?? 0.12;
					const pending = mediaNodes.filter((h) => !(h.__mediaRevealPlayed || h.__mediaRevealPlaying));
					/*
					 * Décalage : media dans une card → calé sur la card (même stagger) ;
					 * sinon images sur une même ligne (2/3/4 côte à côte) → légère cascade gauche → droite.
					 */
					const rows = mediaRowRanks(pending.filter((h) => !itemOf(h)));
					pending.forEach((host) => {
						const it = itemOf(host);
						const at = it
							? cardAt + Math.max(0, items.indexOf(it)) * itemStagger + 0.03
							: mediaAt + (rows.get(host) || 0) * rowStagger;
						tweenMediaReveal(tl, host, this._mobile, at);
					});
				}

				if (decors.length) {
					const decorFade = decors.filter(revealFadeOnly);
					const decorMove = decors.filter((n) => !revealFadeOnly(n));
					if (decorMove.length) {
						tl.to(
							decorMove,
							{
								autoAlpha: 1,
								y: 0,
								duration: motionDuration("decor", this._mobile),
								stagger: motionStagger("decor", this._mobile),
								ease: MOTION.ease.decor,
							},
							"-=0.55"
						);
					}
					if (decorFade.length) {
						tl.to(
							decorFade,
							{
								autoAlpha: 1,
								duration: motionDuration("decor", this._mobile),
								stagger: motionStagger("decor", this._mobile),
								ease: MOTION.ease.decor,
							},
							"-=0.55"
						);
					}
				}
			}

			const hasContent =
				masks.length ||
				titleInners.length ||
				softTitles.length ||
				soft.length ||
				items.length ||
				decors.length ||
				mediaNodes.length ||
				target.__revealSelf;

			if (!hasContent) {
				markDone();
				return;
			}

			/* Timeline vide (inners [] etc.) → onComplete ne suffit pas toujours */
			if (typeof tl.getChildren === "function" && tl.getChildren().length === 0) {
				this._forceShowBlock(target);
			}
		} catch {
			this._forceShowBlock(target);
		}
	}

	async _runMeetReveal(target, markDone) {
		if (!target.__meetLeft || !target.__meetRight) {
			const panes = collectMeetPanes(target);
			target.__meetLeft = panes.left;
			target.__meetRight = panes.right;
			target.__revealMeet = true;
		}

		const left = target.__meetLeft || [];
		const right = target.__meetRight || [];
		const delay = parseFloat(target.getAttribute("data-reveal-delay") || "0") || 0;
		let titleInners = [];
		let softTitles = [];
		try {
			({ titleInners, softTitles } = await this._collectTitleTweens(target));
		} catch {
			queryOwned(target, LINES_SEL).forEach((t) => {
				t.classList.remove("is-reveal-pending");
				t.style.visibility = "visible";
			});
		}
		if (this._destroyed) {
			this._forceShowBlock(target);
			return;
		}
		const vh = window.innerHeight || 0;
		if (isScrolledPast(target, vh)) {
			this._forceShowBlock(target);
			return;
		}
		if (!isInViewportBand(target, vh)) {
			this._rearmBlock(target);
			return;
		}

		if (!left.length && !right.length && !titleInners.length && !softTitles.length) {
			markDone();
			return;
		}

		const settleMeet = () => {
			left.forEach((n) => {
				const isDeco = n.classList?.contains?.("c-steps__deco");
				gsap.set(n, {
					autoAlpha: 1,
					visibility: "visible",
					y: 0,
					zIndex: isDeco ? 2 : 3,
					clearProps: "transform",
				});
			});
			if (right.length) {
				gsap.set(right, {
					autoAlpha: 1,
					visibility: "visible",
					y: 0,
					zIndex: 1,
					clearProps: "transform",
				});
			}
			target.__meetTl = null;
			queryOwned(target, LINES_SEL).forEach((t) => {
				t.classList.remove("is-reveal-pending");
				t.style.visibility = "visible";
				gsap.set(t, { autoAlpha: 1, visibility: "visible", y: 0, clearProps: "transform" });
				this._resetSplitInners(t);
			});
			markDone();
		};

		target.__meetTl?.kill?.();

		const tl = gsap.timeline({
			delay,
			defaults: { ease: MOTION.ease.block, overwrite: "auto" },
			onComplete: settleMeet,
			onInterrupt: settleMeet,
		});
		target.__meetTl = tl;

		const dur = motionDuration("block", this._mobile);
		const lag = this._mobile ? 0.12 : 0.08;

		if (left.length) {
			tl.to(
				left,
				{
					y: 0,
					duration: dur,
					ease: MOTION.ease.block,
					force3D: true,
				},
				0
			);
		}
		if (right.length) {
			tl.to(
				right,
				{
					y: 0,
					duration: dur,
					ease: MOTION.ease.block,
					force3D: true,
				},
				lag
			);
		}

		this._addTitleTweens(tl, titleInners, softTitles, 0);

		/* Filet : settle si le tween ne termine pas */
		const safetyMs = Math.ceil((delay + dur + 0.35) * 1000);
		const meetId = setTimeout(() => {
			this._safetyTimeouts = (this._safetyTimeouts || []).filter((t) => t !== meetId);
			if (this._destroyed || target.__revealed) return;
			settleMeet();
		}, safetyMs);
		(this._safetyTimeouts ||= []).push(meetId);

		if (isInViewportBand(target, window.innerHeight || 0)) {
			this._armBlockSafety(target);
		}
	}

	async _runPanelSlideReveal(target, markDone) {
		if (!target.__panelSlidePanels) {
			target.__panelSlidePanels = collectPanelSlideTargets(target);
			if (isSliderReveal(target)) target.__revealSlider = true;
			if (isTextImagePanelReveal(target)) target.__revealTextImagePanel = true;
		}

		const panels = target.__panelSlidePanels || [];
		const delay = parseFloat(target.getAttribute("data-reveal-delay") || "0") || 0;
		let titleInners = [];
		let softTitles = [];
		try {
			({ titleInners, softTitles } = await this._collectTitleTweens(target));
		} catch {
			queryOwned(target, LINES_SEL).forEach((t) => {
				t.classList.remove("is-reveal-pending");
				t.style.visibility = "visible";
			});
		}
		if (this._destroyed) {
			this._forceShowBlock(target);
			return;
		}
		const vh = window.innerHeight || 0;
		if (isScrolledPast(target, vh)) {
			this._forceShowBlock(target);
			return;
		}
		if (!isInViewportBand(target, vh)) {
			this._rearmBlock(target);
			return;
		}

		if (!panels.length && !titleInners.length && !softTitles.length) {
			markDone();
			return;
		}

		target.__sliderTl?.kill?.();
		const tl = gsap.timeline({
			delay,
			defaults: { ease: MOTION.ease.block },
			onComplete: markDone,
			onInterrupt: markDone,
		});
		target.__sliderTl = tl;

		if (panels.length) {
			tl.to(panels, {
				autoAlpha: 1,
				y: 0,
				duration: motionDuration("block", this._mobile),
				ease: MOTION.ease.block,
				force3D: true,
			});
		}

		this._addTitleTweens(tl, titleInners, softTitles, 0);

		const mediaHosts = [
			...new Set([
				...queryOwned(target, "[data-reveal-media]"),
				...Array.from(target.querySelectorAll?.("[data-reveal-media]") || []),
			]),
		];
		mediaHosts.forEach((host) => {
			if (host.__mediaRevealPlayed || host.__mediaRevealPlaying) return;
			tweenMediaReveal(tl, host, this._mobile, 0);
		});

		if (isInViewportBand(target, window.innerHeight || 0)) {
			this._armBlockSafety(target);
		}
	}

	_runSliderReveal(target, markDone) {
		this._runPanelSlideReveal(target, markDone);
	}

	_armBlockSafety(target) {
		if (!target || target.__revealSafetyArmed) return;
		target.__revealSafetyArmed = true;
		/*
		 * Doit durer PLUS LONG que la TL typique (titre ~0.8 + items stagger ~0.9).
		 * Cap trop court → forceShow au milieu = contenu « visible sans anim ».
		 */
		const ms = Math.max(Math.ceil((MOTION.safetyIntroSec || 2.8) * 1000), 2800);
		const id = setTimeout(() => {
			this._safetyTimeouts = (this._safetyTimeouts || []).filter((t) => t !== id);
			target.__revealSafetyId = null;
			if (!this._destroyed && !target.__revealed) {
				this._forceShowBlock(target);
			}
		}, ms);
		target.__revealSafetyId = id;
		(this._safetyTimeouts ||= []).push(id);
	}

	async _splitTitleLines(title, SplitType) {
		/* Pré-split (idle, à l'init) : réutiliser — pas de layout pendant le scroll */
		if (title.__split && title.__inners?.length && title.__inners[0]?.isConnected) {
			return title.__inners;
		}
		title.__split?.revert?.();
		const { split, inners } = splitTitleIntoLines(title, SplitType);
		this._splits.push(split);
		return inners;
	}

	/**
	 * Pré-découpe toutes les lignes en attente quand le navigateur est libre :
	 * le split (lecture de layout) ne tombe plus au milieu d'un scroll Lenis.
	 */
	_presplitPending() {
		const run = async () => {
			if (this._destroyed) return;
			await (this._assetsReady || preloadRevealAssets());
			const SplitType = await getSplitType().catch(() => null);
			if (!SplitType || this._destroyed) return;
			const blocks = (this.DOM.revealBlocks || []).filter((b) => !b.__revealStarted && !b.__revealed);
			blocks.forEach((b) => {
				if (isMeetReveal(b) || isPanelSlideReveal(b)) return;
				queryOwned(b, LINES_SEL).forEach((t) => {
					if (t.__split || t.__revealDone) return;
					if (!shouldUseLineMask(t, { mobile: this._mobile })) return;
					const { split } = splitTitleIntoLines(t, SplitType);
					this._splits.push(split);
				});
			});
		};
		if (typeof requestIdleCallback === "function") {
			requestIdleCallback(() => run(), { timeout: 1200 });
		} else {
			setTimeout(run, 200);
		}
	}

	/** Revert du split hors de la frame courante (pas pendant un rendu de scroll). */
	_revertTitleSplitIdle(title) {
		if (!title) return;
		title.__revealDone = true;
		const go = () => this._revertTitleSplit(title);
		if (typeof requestIdleCallback === "function") {
			requestIdleCallback(go, { timeout: 600 });
		} else {
			setTimeout(go, 60);
		}
	}

	/** Retire le split (HTML d'origine) une fois le titre révélé. */
	_revertTitleSplit(title) {
		if (!title) return;
		const split = title.__split || title._splitInstance;
		title.__revealDone = true;
		if (!split) return;
		try {
			split.revert?.();
		} catch {
			/* ignore */
		}
		title.__split = null;
		title._splitInstance = null;
		title.__inners = [];
		this._splits = (this._splits || []).filter((sp) => sp !== split);
	}

	/* --------------------------- orphan .st2 (title-only sections) --------------------------- */
	async prepareOrphanTitles() {
		if (!this.DOM.orphanTitles.length) return;

		await waitFonts();
		if (this._destroyed) return;
		let SplitType = null;

		for (const title of this.DOM.orphanTitles) {
			if (!shouldUseLineMask(title, { mobile: this._mobile })) {
				this._prepareSoftTitle(title);
				continue;
			}
			SplitType ||= await getSplitType();
			title.__split?.revert?.();
			const { split } = splitTitleIntoLines(title, SplitType);
			this._splits.push(split);
			title.classList.add("is-reveal-pending");
			gsap.set(title, { visibility: "hidden", autoAlpha: 1, y: 0 });
		}

		if (!this._ioTitles) {
			this._ioTitles = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) return;
						this.animateOrphanTitle(entry.target);
						this._ioTitles.unobserve(entry.target);
					});
				},
				{ root: null, threshold: motionIo("titles", this._mobile).threshold, rootMargin: motionIo("titles", this._mobile).rootMargin }
			);
		}
		this.DOM.orphanTitles.forEach((t) => this._ioTitles.observe(t));
		this._flushVisibleOrphans();
		this._bindResizeIfNeeded();
	}

	animateOrphanTitle(title) {
		if (!this._gateOpen || pageStillCovered()) return;
		if (title.__useLineMask === false) {
			gsap.set(title, { visibility: "visible" });
			title.classList.remove("is-reveal-pending");
			gsap.to(title, {
				autoAlpha: 1,
				y: 0,
				duration: motionDuration("soft", this._mobile),
				ease: MOTION.ease.soft,
				overwrite: "auto",
			});
			return;
		}

		const inners = title.__inners;
		if (!inners?.length) {
			gsap.set(title, { visibility: "visible" });
			title.classList.remove("is-reveal-pending");
			return;
		}
		gsap.set(title, { visibility: "visible" });
		title.classList.remove("is-reveal-pending");
		gsap.to(inners, {
			...titleLinesTween(inners, this._mobile, () => this._revertTitleSplitIdle(title)),
			overwrite: "auto",
		});
	}

	/* --------------------------- resize --------------------------- */
	_bindResizeIfNeeded() {
		if (this._onResize) return;
		this._onResize = () => {
			clearTimeout(this._resizeTimer);
			this._resizeTimer = setTimeout(async () => {
				try {
					if (this._destroyed || this._skipHeavy || prefersReducedMotion()) return;
					if (this._introStarted && !this._introDone) return;
					/* Lignes = fonction de la largeur : re-split à chaque changement de largeur */
					const w = window.innerWidth || 0;
					if (w === this._lastSplitWidth) return;
					this._lastSplitWidth = w;
					this._breakpointBucket = getBreakpointBucket();
					this._mobile = isCoarseOrNarrow();

					await waitFonts();
					/*
					 * Titres déjà révélés : split retiré → reflow natif, rien à faire.
					 * Titres en attente : re-split (les lignes dépendent de la largeur).
					 */
					const titles = Array.from(this.$el.querySelectorAll(LINES_SEL));
					let SplitType = null;
					for (const title of titles) {
						if (title.__revealDone || !title.__split) continue;
						/* En cours d'animation (bloc démarré) : ne pas casser le tween */
						const host = title.closest?.("[data-reveal]");
						if (host?.__revealStarted && !host?.__revealed) continue;
						SplitType ||= await getSplitType();
						if (!SplitType) break;
						const prev = title.__split;
						prev?.revert?.();
						this._splits = this._splits.filter((sp) => sp !== prev);
						const { split } = splitTitleIntoLines(title, SplitType);
						this._splits.push(split);
					}
				} catch {
					/* ignore */
				}
			}, 280);
		};
		window.addEventListener("resize", this._onResize, { passive: true });

		this._onVisibilityChange = () => {
			if (document.hidden || !this.$header) return;
			if (!this.DOM.titlesSt1?.length) return;
			const isHidden = this.DOM.titlesSt1.some((h1) => h1.style.visibility === "hidden");
			if (isHidden && !this._introDone && !this._introStarted) {
				this._start();
			} else if (this._introDone) {
				this.showAllImmediate();
			}
		};
		document.addEventListener("visibilitychange", this._onVisibilityChange);
	}

	destroy() {
		this._destroyed = true;
		try {
			this._ioLate?.disconnect?.();
			this._ioLate = null;
		} catch {
			/* ignore */
		}
		try {
			if (this._safetyTimer) clearTimeout(this._safetyTimer);
			this._safetyTimer = null;
			if (this._loaderGuard) clearTimeout(this._loaderGuard);
			this._loaderGuard = null;
			this._introSafety?.kill?.();
			this._introSafety = null;
			(this._safetyTimeouts || []).forEach((id) => clearTimeout(id));
			this._safetyTimeouts = [];

			if (this._onLoaderReveal) {
				window.removeEventListener("site-loader:reveal", this._onLoaderReveal);
				this._onLoaderReveal = null;
			}
			if (this._onLoaderDone) {
				window.removeEventListener("site-loader:done", this._onLoaderDone);
				this._onLoaderDone = null;
			}
			if (this._onEnableScroll) {
				this.app?.off?.("enable-scroll", this._onEnableScroll);
				this._onEnableScroll = null;
			}
			if (this._onTransitionDone) {
				window.removeEventListener("page-transition:done", this._onTransitionDone);
				this._onTransitionDone = null;
			}
			if (this._onTransitionReveal) {
				window.removeEventListener("page-transition:reveal", this._onTransitionReveal);
				this._onTransitionReveal = null;
			}
			if (this._onAppFlush) {
				this.app?.off?.("scroll", this._onAppFlush);
				this._onAppFlush = null;
			}
			if (this._onWinFlush) {
				window.removeEventListener("scroll", this._onWinFlush);
				this._onWinFlush = null;
			}
			if (this._scrollFlushRaf) {
				cancelAnimationFrame(this._scrollFlushRaf);
				this._scrollFlushRaf = null;
			}
			this._scrollFlushBound = false;

			this._ioBlocks?.disconnect();
			this._ioTitles?.disconnect();
			window.removeEventListener("resize", this._onResize);
			document.removeEventListener("visibilitychange", this._onVisibilityChange);
			this._introTl?.kill?.();
			this._heroMediaTl?.kill?.();
			this._heroMediaTl = null;
			this._splits.forEach((s) => s.revert?.());
			this._splits = [];
			this._revealQueue = [];
			this._activeRevealCount = 0;

			/* Clear GSAP hide state — footer/Barba ne doivent pas laisser autoAlpha:0 */
			const host = this.$el;
			if (host?.querySelectorAll) {
				queryOwned(host, "[data-reveal-item], [data-reveal-soft], [data-reveal-decor], [data-reveal-pane]").forEach(
					(n) => {
						gsap.set(n, { autoAlpha: 1, clearProps: "transform,opacity,visibility" });
					}
				);
				queryOwned(host, LINES_SEL).forEach((t) => {
					t.classList.remove("is-reveal-pending");
					t.style.visibility = "";
					gsap.set(t, { clearProps: "all" });
				});
			}
		} catch {
			/* ignore */
		}

		this.DOM.titlesSt1?.forEach((h1) => {
			h1._splitInstance?.revert?.();
			h1._splitInstance = null;
			h1.style.visibility = "";
			h1.classList.remove("is-reveal-pending");
		});
		this.DOM.titlesSt2?.forEach((t) => {
			t.__split?.revert?.();
			t.__split = null;
			t.classList.remove("is-reveal-pending");
		});
		if (this.DOM.surtitle) gsap.set(this.DOM.surtitle, { clearProps: "all" });
		if (this.DOM.desc) gsap.set(this.DOM.desc, { clearProps: "all" });
		if (this.DOM.btns?.length) gsap.set(this.DOM.btns, { clearProps: "all" });
		this._introDone = false;
		this._introTl = null;
	}
}
